'use client'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'

import { useGamePassSummary } from '@/components/dashboard/game-pass-card'
import { SevenDayCheckInStrip } from '@/components/check-in/seven-day-strip'
import { GamePassFundingChoice } from '@/components/game-pass-funding'
import { BrawlRosterConfiguration } from '@/components/brawl-roster-configuration'
import { CustomRosterConfiguration } from '@/components/custom-roster-configuration'
import { JevRoutingStatus } from '@/components/jev-routing-status'
import { customRosterSelectionKeys, customRosterSelections, customRosterVillagerKeys } from '@/lib/custom-roster-selection'
import { TournamentEntry } from '@/components/tournament/entry'
import { TournamentLauncher } from '@/components/tournament/launcher'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { runSingleFlight, useSingleFlight } from '@/lib/single-flight'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAuth } from '@/lib/auth'
import { useAccountTimeZone } from '@/hooks/use-account-time-zone'
import { useAccountDayClock } from '@/hooks/use-account-day-clock'
import { buildSupportContactUrl } from '@/lib/arena'
import {
  ANONYMOUS_FREE_GAME_USAGE_STORAGE_KEY,
  readAnonymousFreeGamesUsed,
  recordAnonymousFreeGameStartedInWindow,
} from '@/lib/anonymous-free-game-usage'
import { useI18n } from '@/lib/i18n'
import { gameStartButtonClassName } from '@/lib/game-start-button-style'
import { returnTargetFrom, signInUrlReturningTo, verifyEmailUrlReturningTo } from '@/lib/return-target'
import {
  buildModelSeatDescriptors,
  getRoleModelRerollContext,
  getResponsiveModelPoolSignature,
  isExplicitHumanModelSeat,
  normalizeSeatAssignments,
  reconcileRoleModelAssignments,
  sameRoleModelAssignments,
  sampleComparisonModels,
  splitByomSeatAssignments,
} from '@/lib/role-model-selection'
import { getStartedGameRoute } from '@/lib/started-game-route'
import {
  countRoleSeats,
  expandWolfSiblingChoice,
  isWolfSiblingSeatKey,
  roleCountsForChoices,
  WOLF_SIBLING_PAIR_KEY,
} from '@/lib/wolf-sibling-configuration'
import { useTRPC } from '@/lib/trpc.ts'
import type { ByomProviderConfig } from '@/components/dashboard/byom-tab'
import { readRoster, readSettingMode, writeSetting, buildCustomRosterSetting, type CustomRosterPlayerCount, ANONYMOUS_FREE_GAME_SETTING, FIXED_GAME_ROSTERS, FREE_GAMES_PER_WINDOW, FREE_GAME_SETTINGS_BY_PLAY_MODE, FREE_GAME_MAX_PLAYERS, GameSettingOptions, JEV_COMPARISON_MODEL, LanguageType, PlayerRoleType, ROLE_EMOJIS, ROOM_NAME_MAX_LENGTH, RoleFaction, generateRoleCodeStringFromCheckbox, getFreeGameQuotaWindowStart, getNextFreeGameQuotaReset, isFixedGameSetting, isJevComparisonModel } from '@repo/sdk'
import { TRPCClientError } from '@trpc/client'
import { Check, Crown, Globe, Loader2, Lock, Play } from 'lucide-react'

const ROSTER_CHOICES = readRoster({ kind: 'catalog' }).choices
const MAX_PLAYERS = 12
const RESUME_GAMES_PATH = '/dashboard?tab=games&status=active'
const LAST_PLAY_MODE_STORAGE_KEY = 'mentiss_last_play_mode'
const LAST_BILLING_MODE_STORAGE_KEY = 'mentiss_last_billing_mode'
const HAS_SHERIFF_STORAGE_KEY = 'mentiss_has_sheriff'
const GAME_CONFIG_STORAGE_PREFIX = 'mentiss_config_'
const DEFAULT_GAME_SETTING = GameSettingOptions.G6_1SR1WT_2WW_2VG
const TEN_SEAT_BOARD_SETTINGS: Record<string, string> = {
  [GameSettingOptions.G12_NIGHTMARE]: GameSettingOptions.G10_NIGHTMARE,
  [GameSettingOptions.G12_DREAMER]: GameSettingOptions.G10_DREAMER,
  [GameSettingOptions.G12_PSYCHIC_MECHANICAL]: GameSettingOptions.G10_PSYCHIC_MECHANICAL,
  [GameSettingOptions.G12_WOLF_BROTHERS_BLACK_MARKET]: GameSettingOptions.G10_WOLF_BROTHERS_BLACK_MARKET,
  [GameSettingOptions.G12_WOLF_BEAUTY_KNIGHT]: GameSettingOptions.G10_WOLF_BEAUTY_KNIGHT,
  [GameSettingOptions.G12_GARGOYLE_GRAVEKEEPER]: GameSettingOptions.G10_GARGOYLE_GRAVEKEEPER,
  [GameSettingOptions.G12_BLOOD_MOON_DEMON_HUNTER]: GameSettingOptions.G10_BLOOD_MOON_DEMON_HUNTER,
  [GameSettingOptions.G12_ALPHA_WOLF_MAGICIAN]: GameSettingOptions.G10_ALPHA_WOLF_MAGICIAN,
  [GameSettingOptions.G12_WRAITH_KNIGHT]: GameSettingOptions.G10_WRAITH_KNIGHT,
  [GameSettingOptions.G12_SNOW_WOLF_ALPHA_KNIGHT]: GameSettingOptions.G10_SNOW_WOLF_ALPHA_KNIGHT,
}
const TWELVE_SEAT_BOARD_SETTINGS: Record<string, string> = Object.fromEntries(
  Object.entries(TEN_SEAT_BOARD_SETTINGS).map(([twelveSeat, tenSeat]) => [tenSeat, twelveSeat]),
)
function isVariableSeatBoard(gameSetting: string): boolean {
  return gameSetting in TEN_SEAT_BOARD_SETTINGS
    || gameSetting in TWELVE_SEAT_BOARD_SETTINGS
}
function tenSeatSettingFor(gameSetting: string): string {
  if ((readSettingMode(gameSetting).symbolicPlayerCount !== null))
    return GameSettingOptions.G10_ULTIMATE_TRIAL
  return TEN_SEAT_BOARD_SETTINGS[gameSetting] ?? gameSetting
}
function isWolfSiblingBoard(gameSetting: string): boolean {
  return tenSeatSettingFor(gameSetting)
    === GameSettingOptions.G10_WOLF_BROTHERS_BLACK_MARKET
}
function boardTabSettingFor(gameSetting: string): string {
  if ((readSettingMode(gameSetting).symbolicPlayerCount !== null))
    return GameSettingOptions.G10_ULTIMATE_TRIAL
  return TWELVE_SEAT_BOARD_SETTINGS[gameSetting] ?? gameSetting
}
const MULTIPLAYER_SETTINGS = new Set<string>([
  GameSettingOptions.G6_1SR1WT_2WW_2VG,
  GameSettingOptions.G9_1HT1SR1WT_3WW_3VG,
  GameSettingOptions.G10_ULTIMATE_TRIAL,
  GameSettingOptions.G12_ULTIMATE_TRIAL,
  GameSettingOptions.G10_NIGHTMARE,
  ...Object.keys(TEN_SEAT_BOARD_SETTINGS),
  ...Object.values(TEN_SEAT_BOARD_SETTINGS),
  GameSettingOptions.USER_CUSTOM_CONFIG,
  GameSettingOptions.HUMAN_VS_AI,
])

const PLATFORM_ROLE_MODEL_SETTINGS = new Set<string>([
  GameSettingOptions.G6_1SR1WT_2WW_2VG,
  GameSettingOptions.G9_1HT1SR1WT_3WW_3VG,
  GameSettingOptions.G10_ULTIMATE_TRIAL,
  GameSettingOptions.G12_ULTIMATE_TRIAL,
  GameSettingOptions.G10_NIGHTMARE,
  ...Object.keys(TEN_SEAT_BOARD_SETTINGS),
  ...Object.values(TEN_SEAT_BOARD_SETTINGS),
  GameSettingOptions.USER_CUSTOM_CONFIG,
  GameSettingOptions.HUMAN_VS_AI,
  GameSettingOptions.WATCH_MODE,
])

type PlayMode = 'solo' | 'multiplayer'

const SHERIFF_FIXED_ELIGIBLE_SETTINGS = new Set<string>([
  GameSettingOptions.G6_1SR1WT_2WW_2VG,
  GameSettingOptions.G9_1HT1SR1WT_3WW_3VG,
  GameSettingOptions.G10_ULTIMATE_TRIAL,
  GameSettingOptions.G12_ULTIMATE_TRIAL,
  GameSettingOptions.G10_NIGHTMARE,
  ...Object.keys(TEN_SEAT_BOARD_SETTINGS),
  ...Object.values(TEN_SEAT_BOARD_SETTINGS),
])

const SHERIFF_CUSTOM_ELIGIBLE_SETTINGS = new Set<string>([
  GameSettingOptions.USER_CUSTOM_CONFIG,
  GameSettingOptions.HUMAN_VS_AI,
  GameSettingOptions.WATCH_MODE,
  GameSettingOptions.MODEL_COMPARISON,
  GameSettingOptions.BYOM,
])

export function isSheriffEligibleGameSetting(
  gameSetting: string,
  playerCount: number,
): boolean {
  if (SHERIFF_FIXED_ELIGIBLE_SETTINGS.has(gameSetting))
    return true
  if (gameSetting === GameSettingOptions.HUMAN_VS_AI)
    return playerCount > 0
  return SHERIFF_CUSTOM_ELIGIBLE_SETTINGS.has(gameSetting) && playerCount >= 9
}

const TAB_TO_SETTING: Record<string, string> = {
  standard6: GameSettingOptions.G6_1SR1WT_2WW_2VG,
  standard10: GameSettingOptions.G9_1HT1SR1WT_3WW_3VG,
  ultimate: GameSettingOptions.G10_ULTIMATE_TRIAL,
  nightmare: GameSettingOptions.G12_NIGHTMARE,
  dreamer: GameSettingOptions.G12_DREAMER,
  psychicMechanical: GameSettingOptions.G12_PSYCHIC_MECHANICAL,
  wolfBrothersBlackMarket: GameSettingOptions.G12_WOLF_BROTHERS_BLACK_MARKET,
  wolfBeautyKnight: GameSettingOptions.G12_WOLF_BEAUTY_KNIGHT,
  gargoyleGravekeeper: GameSettingOptions.G12_GARGOYLE_GRAVEKEEPER,
  bloodMoonDemonHunter: GameSettingOptions.G12_BLOOD_MOON_DEMON_HUNTER,
  alphaWolfMagician: GameSettingOptions.G12_ALPHA_WOLF_MAGICIAN,
  wraithKnight: GameSettingOptions.G12_WRAITH_KNIGHT,
  custom: GameSettingOptions.USER_CUSTOM_CONFIG,
  humanvsai: GameSettingOptions.HUMAN_VS_AI,
  watch: GameSettingOptions.WATCH_MODE,
  comparison: GameSettingOptions.MODEL_COMPARISON,
  byom: GameSettingOptions.BYOM,
}
const SETTING_TO_TAB: Record<string, string> = Object.fromEntries(
  Object.entries(TAB_TO_SETTING).map(([k, v]) => [v, k]),
)
SETTING_TO_TAB[GameSettingOptions.G12_ULTIMATE_TRIAL] = 'ultimate'
for (const [twelveSeat, tenSeat] of Object.entries(TEN_SEAT_BOARD_SETTINGS)) {
  SETTING_TO_TAB[tenSeat] = SETTING_TO_TAB[twelveSeat]
}

type BillingMode = 'free' | 'quick' | 'expert'

type StartAccessResolution = 'signin' | 'topup' | 'switch-to-paid'

interface StartAccessRestriction {
  resolution: StartAccessResolution
  reasonKey: string
}

interface StoredGameConfiguration {
  roles?: string[]
  villagers?: number[]
  yourRole?: string
  hasSheriff?: boolean
  byomRoleAssignments?: Record<string, string>
  playerCount?: CustomRosterPlayerCount
  roleModelAssignments?: Record<string, string>
}

function isBillingMode(value: string | null): value is BillingMode {
  return value === 'free' || value === 'quick' || value === 'expert'
}

function getStoredBillingMode(): BillingMode | null {
  try {
    const value = localStorage.getItem(LAST_BILLING_MODE_STORAGE_KEY)
    return isBillingMode(value) ? value : null
  }
  catch {
    return null
  }
}

function getStoredPlayMode(): PlayMode {
  try {
    return localStorage.getItem(LAST_PLAY_MODE_STORAGE_KEY) === 'multiplayer'
      ? 'multiplayer'
      : 'solo'
  }
  catch {
    return 'solo'
  }
}

function getInitialGameSetting(): string {
  const playMode = getStoredPlayMode()
  const tabParam = new URLSearchParams(window.location.search).get('tab')
  if (tabParam && TAB_TO_SETTING[tabParam]) {
    const linkedSetting = TAB_TO_SETTING[tabParam]
    try {
      const saved = localStorage.getItem('lastGameSetting')
      if (
        saved
        && boardTabSettingFor(saved) === linkedSetting
        && (Object.values(GameSettingOptions) as string[]).includes(saved)
        && !saved.includes('SNOW_WOLF_ALPHA')
      ) {
        return (getStoredBillingMode() ?? 'free') === 'free'
          ? tenSeatSettingFor(saved)
          : saved
      }
    }
    catch {}
    return (getStoredBillingMode() ?? 'free') === 'free'
      ? tenSeatSettingFor(linkedSetting)
      : linkedSetting
  }

  try {
    const saved = localStorage.getItem('lastGameSetting')
    if (
      saved
      && (Object.values(GameSettingOptions) as string[]).includes(saved)
      && !saved.includes('SNOW_WOLF_ALPHA')
    ) {
      const selectedSetting = (getStoredBillingMode() ?? 'free') === 'free'
        ? tenSeatSettingFor(saved)
        : saved
      if (
        (getStoredBillingMode() ?? 'free') === 'free'
        && !readRoster({ kind: 'selection', setting: selectedSetting, playMode }).freeGameEligible
      ) {
        return DEFAULT_GAME_SETTING
      }
      return selectedSetting
    }
  }
  catch {}

  return DEFAULT_GAME_SETTING
}

function readLegacySheriffPreference(): {
  setting: string | null
  hasSheriff: boolean
} {
  try {
    const setting = localStorage.getItem('lastGameSetting')
    const storedValue = localStorage.getItem(HAS_SHERIFF_STORAGE_KEY)
    return {
      setting,
      hasSheriff: storedValue === 'true',
    }
  }
  catch {
    return { setting: null, hasSheriff: false }
  }
}

interface RoleConfiguration {
  goodRoles: Array<{ key: string, count: number, enabled: boolean }>
  evilRoles: Array<{ key: string, count: number, enabled: boolean }>
  villagerCount: number
}

function isRoleDisabledForHumanVsAi(gameSetting: string, roleKey: string): boolean {
  return gameSetting === 'HUMAN_VS_AI'
    && expandWolfSiblingChoice(roleKey).some(role => (ROSTER_CHOICES.humanVsAiDisabledRoles as readonly string[]).includes(role))
}

function usesCustomRosterTemplate(gameSetting: string): boolean {
  return readSettingMode(gameSetting).namedMode !== null
}

function getRoleConfiguration(gameSetting: string): RoleConfiguration {
  if (isFixedGameSetting(gameSetting)) {
    const goodRoles: RoleConfiguration['goodRoles'] = []
    const evilRoles: RoleConfiguration['evilRoles'] = []
    const seen = new Set<string>()
    let villagerCount = 0
    let werewolfCount = 0
    for (const role of FIXED_GAME_ROSTERS[gameSetting]) {
      if (role === 'villager') {
        villagerCount++
        continue
      }
      const key = role === 'werewolf'
        ? werewolfCount++ === 0 ? 'werewolf' : `werewolf_${werewolfCount}`
        : role
      if (seen.has(key))
        continue
      seen.add(key)
      const entry = { key, count: 1, enabled: true }
      if (RoleFaction[role as keyof typeof RoleFaction] === 'good')
        goodRoles.push(entry)
      else
        evilRoles.push(entry)
    }
    return { goodRoles, evilRoles, villagerCount }
  }

  switch (gameSetting) {
    case 'G6_1SR1WT_2WW_2VG': {
      return {
        goodRoles: [
          { key: 'seer', count: 1, enabled: true },
          { key: 'witch', count: 1, enabled: true },
        ],
        evilRoles: [
          { key: 'werewolf', count: 1, enabled: true },
          { key: 'werewolf_2', count: 1, enabled: true },
        ],
        villagerCount: 2,
      }
    }
    case 'G9_1HT1SR1WT_3WW_3VG': {
      return {
        goodRoles: [
          { key: 'seer', count: 1, enabled: true },
          { key: 'witch', count: 1, enabled: true },
          { key: 'hunter', count: 1, enabled: true },
        ],
        evilRoles: [
          { key: 'werewolf', count: 1, enabled: true },
          { key: 'werewolf_2', count: 1, enabled: true },
          { key: 'werewolf_3', count: 1, enabled: true },
        ],
        villagerCount: 3,
      }
    }
    case 'G10_ULTIMATE_TRIAL':
    case 'G12_ULTIMATE_TRIAL': {
      return {
        goodRoles: [...ROSTER_CHOICES.fixed, ...ROSTER_CHOICES.extraGodChoices]
          .map(key => ({ key, count: 1, enabled: false })),
        evilRoles: [...ROSTER_CHOICES.wolfCatalog.filter(key => key !== 'mechanical_wolf'), 'werewolf', 'werewolf_2', 'werewolf_3']
          .map(key => ({ key, count: key === WOLF_SIBLING_PAIR_KEY ? 2 : 1, enabled: false })),
        villagerCount: readSettingMode(gameSetting).symbolicPlayerCount === 12 ? 4 : 3,
      }
    }
    case 'USER_CUSTOM_CONFIG':
    case 'HUMAN_VS_AI':
    case 'WATCH_MODE':
    case 'MODEL_COMPARISON':
    case 'BYOM': {
      return {
        goodRoles: [...ROSTER_CHOICES.fixed, ...ROSTER_CHOICES.extraGodChoices]
          .map(key => ({ key, count: 1, enabled: true })),
        evilRoles: [...ROSTER_CHOICES.wolfCatalog, 'werewolf', 'werewolf_2', 'werewolf_3', 'werewolf_4']
          .map(key => ({ key, count: key === WOLF_SIBLING_PAIR_KEY ? 2 : 1, enabled: !isRoleDisabledForHumanVsAi(gameSetting, key) })),
        villagerCount: 8,
      }
    }
    default: {
      return {
        goodRoles: [],
        evilRoles: [],
        villagerCount: 0,
      }
    }
  }
}

function getStoredGameConfiguration(
  gameSetting: string,
): StoredGameConfiguration | null {
  try {
    const raw = localStorage.getItem(
      `${GAME_CONFIG_STORAGE_PREFIX}${(readSettingMode(gameSetting).symbolicPlayerCount !== null) ? GameSettingOptions.G10_ULTIMATE_TRIAL : gameSetting}`,
    )
    if (!raw)
      return null

    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null
    }

    const stored = parsed as Record<string, unknown>
    const roleConfiguration = getRoleConfiguration(gameSetting)
    const allowedRoleKeys = new Set(
      [...roleConfiguration.goodRoles, ...roleConfiguration.evilRoles]
        .filter(role => role.enabled || usesCustomRosterTemplate(gameSetting) || (readSettingMode(gameSetting).symbolicPlayerCount !== null))
        .map(role => role.key),
    )
    const allowedPlayerRoles = new Set(
      Array.from(allowedRoleKeys).flatMap(roleKey =>
        ['werewolf_2', 'werewolf_3', 'werewolf_4'].includes(roleKey)
          ? ['werewolf']
          : expandWolfSiblingChoice(roleKey),
      ),
    )
    if (roleConfiguration.villagerCount > 0) {
      allowedPlayerRoles.add('villager')
    }

    const result: StoredGameConfiguration = {}
    if (stored.playerCount === 10 || stored.playerCount === 12)
      result.playerCount = stored.playerCount
    if (Array.isArray(stored.roles)) {
      result.roles = stored.roles.filter(
        (role): role is string =>
          typeof role === 'string' && (allowedRoleKeys.has(role)
            || (usesCustomRosterTemplate(gameSetting) && isWolfSiblingSeatKey(role))),
      )
    }
    if (Array.isArray(stored.villagers)) {
      result.villagers = stored.villagers.filter(
        (index): index is number =>
          Number.isInteger(index) && Number(index) >= 0 && Number(index) < 8,
      ) as number[]
    }
    if (
      typeof stored.yourRole === 'string'
      && (stored.yourRole === '' || allowedPlayerRoles.has(stored.yourRole))
    ) {
      result.yourRole = stored.yourRole
    }
    if (typeof stored.hasSheriff === 'boolean') {
      result.hasSheriff = stored.hasSheriff
    }
    if (stored.roleModelAssignments && typeof stored.roleModelAssignments === 'object'
      && !Array.isArray(stored.roleModelAssignments)) {
      result.roleModelAssignments = Object.fromEntries(Object.entries(stored.roleModelAssignments)
        .filter(([key, value]) => (allowedRoleKeys.has(key) || allowedPlayerRoles.has(key) || /^villager_[0-7]$/.test(key))
          && typeof value === 'string'))
    }
    if (
      stored.byomRoleAssignments
      && typeof stored.byomRoleAssignments === 'object'
      && !Array.isArray(stored.byomRoleAssignments)
    ) {
      result.byomRoleAssignments = Object.fromEntries(
        Object.entries(stored.byomRoleAssignments).filter(
          ([key, value]) =>
            (allowedRoleKeys.has(key)
              || allowedPlayerRoles.has(key)
              || /^villager_[0-7]$/.test(key))
            && typeof value === 'string',
        ),
      )
    }

    return result
  }
  catch {
    return null
  }
}

function getRoleName(t: (key: string) => string, roleKey: string): string {
  if (roleKey === 'black_market_dealer') {
    return t('game.black_market_dealer_display')
  }

  if (['werewolf_2', 'werewolf_3', 'werewolf_4'].includes(roleKey)) {
    const emoji = ROLE_EMOJIS['werewolf' as keyof typeof ROLE_EMOJIS] || ''
    const roleName = t('game.werewolf')
    return `${emoji} ${roleName}`
  }

  const emoji = ROLE_EMOJIS[roleKey as keyof typeof ROLE_EMOJIS] || ''
  const roleName = t(`game.${roleKey}`) || t(roleKey) || roleKey
  return `${emoji} ${roleName}`
}

function getModelSeatName(t: (key: string) => string, seatKey: string): string {
  if (seatKey === 'werewolf')
    return `${getRoleName(t, seatKey)} #1`
  const werewolfMatch = seatKey.match(/^werewolf_(\d+)$/)
  if (werewolfMatch)
    return `${getRoleName(t, seatKey)} #${werewolfMatch[1]}`
  const villagerMatch = seatKey.match(/^villager_(\d+)$/)
  if (villagerMatch) {
    return `${getRoleName(t, 'villager')} #${Number(villagerMatch[1]) + 1}`
  }
  return getRoleName(t, seatKey)
}

interface GameSectionProps {
  headingLevel?: 1 | 2
}

function pickRandom(arr: string[]): string {
  if (arr.length === 0)
    return ''
  return arr[Math.floor(Math.random() * arr.length)]!
}

export function GameSection({ headingLevel = 1 }: GameSectionProps) {
  const Heading = headingLevel === 1 ? 'h1' : 'h2'
  const { t, language } = useI18n()
  const [searchParams, setSearchParams] = useSearchParams()
  const legacySheriffPreference = useRef(readLegacySheriffPreference())
  const [gameSettingForGame, setGameSettingForGame] = useState(
    getInitialGameSetting,
  )

  const [role, setRole] = useState(() => {
    const initSetting = getInitialGameSetting()
    const storedConfig = getStoredGameConfiguration(initSetting)
    if (storedConfig?.yourRole)
      return storedConfig.yourRole

    if (
      initSetting === 'G6_1SR1WT_2WW_2VG'
      || initSetting === 'G9_1HT1SR1WT_3WW_3VG'
      || isFixedGameSetting(initSetting)
    ) {
      return 'werewolf'
    }
    return ''
  })
  const [validationError, setValidationError] = useState('')
  const [roomName, setRoomName] = useState('')
  const [roomPaymentMode, setRoomPaymentMode] = useState<'split' | 'host'>('split')
  const roomNameInputRef = useRef<HTMLInputElement>(null)
  const [playMode, setPlayMode] = useState<PlayMode>(getStoredPlayMode)
  const isMultiplayer = playMode === 'multiplayer'
  const supportsPlatformRoleModelSelection
    = PLATFORM_ROLE_MODEL_SETTINGS.has(gameSettingForGame)

  const [selectedD10Roles, setSelectedD10Roles] = useState<Set<string>>(
    () => new Set(),
  )
  const [selectedD10Villagers, setSelectedD10Villagers] = useState<Set<number>>(
    () => new Set([0, 1, 2]),
  )
  const [selectedCustomRoles, setSelectedCustomRoles] = useState<Set<string>>(
    () => new Set(),
  )
  const [selectedCustomVillagers, setSelectedCustomVillagers] = useState<
    Set<number>
  >(() => new Set())
  const [customPlayerCount, setCustomPlayerCount] = useState<CustomRosterPlayerCount>(10)
  const { extraRole: customExtraRole, wolfChoice: customWolfChoice } = customRosterSelections(selectedCustomRoles)
  const [roleCodeString, setRoleCodeString] = useState<string>('')
  const [isConfigLoaded, setIsConfigLoaded] = useState(false)
  const [hasSheriff, setHasSheriff] = useState(() => {
    const initSetting = getInitialGameSetting()
    const storedConfig = getStoredGameConfiguration(initSetting)
    if (typeof storedConfig?.hasSheriff === 'boolean') {
      return storedConfig.hasSheriff
    }
    return legacySheriffPreference.current.setting === initSetting
      ? legacySheriffPreference.current.hasSheriff
      : false
  })
  const [showModelConfiguration, setShowModelConfiguration] = useState(false)
  const [billingMode, setBillingMode] = useState<BillingMode>(() => {
    const tabParam = new URLSearchParams(window.location.search).get('tab')
    const deepLinkSetting = tabParam ? TAB_TO_SETTING[tabParam] : undefined
    const storedBillingMode = getStoredBillingMode()
    const isPaidOnlyDeepLink
      = deepLinkSetting
        && !readRoster({ kind: 'selection', setting: tenSeatSettingFor(deepLinkSetting), playMode: getStoredPlayMode() }).freeGameEligible

    if (isPaidOnlyDeepLink && storedBillingMode === 'free') {
      return 'quick'
    }
    return storedBillingMode ?? (isPaidOnlyDeepLink ? 'quick' : 'free')
  })
  const isFreeBillingMode = billingMode === 'free'
  const isExpertBillingMode = billingMode === 'expert'
  const isModelConfigurationVisible
    = !isFreeBillingMode && showModelConfiguration
  const billingModeOptions: Array<{
    value: BillingMode
    titleKey: string
  }> = [
    {
      value: 'free',
      titleKey: 'game.freeMode',
    },
    {
      value: 'quick',
      titleKey: 'game.quickMode',
    },
    {
      value: 'expert',
      titleKey: 'game.expertMode',
    },
  ]
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const {
    user,
    actor,
    isAnonymous,
    authResolved,
    signInAnonymously,
  } = useAuth()
  const redirectToEmailVerificationIfRequired = useCallback(() => {
    if (!user || user.gameIdentityVerified)
      return false
    navigate(verifyEmailUrlReturningTo(returnTargetFrom(window.location)))
    return true
  }, [navigate, user])
  const [anonymousBrowserFreeGamesUsed, setAnonymousBrowserFreeGamesUsed]
    = useState(() => readAnonymousFreeGamesUsed())
  const accountCalendar = useAccountTimeZone()

  const { mutateAsync: startGameAsync, isPending: isStarting } = useMutation(
    trpc.playRouter.start.mutationOptions(),
  )

  const { mutateAsync: createRoomAsync, isPending: isCreatingRoom }
    = useMutation(trpc.roomRouter.create.mutationOptions())

  const { data: isAdmin } = useQuery({
    ...trpc.userRouter.isAdmin.queryOptions(),
    enabled: !!user,
  })

  const {
    data: restrictionsData,
    isFetching: restrictionsAreFetching,
    refetch: refetchRestrictions,
  } = useQuery({
    ...trpc.userRouter.getGameRestrictions.queryOptions(),
    enabled: accountCalendar.ready,
    refetchOnWindowFocus: true,
    refetchInterval: 5 * 60 * 1000,
  })
  const accountTimeZone = actor
    ? restrictionsData?.timeZone ?? accountCalendar.timeZone
    : accountCalendar.timeZone
  const {
    nowMs: quotaServerNowMs,
    currentTime: currentQuotaTime,
    refresh: refreshQuotaClock,
  } = useAccountDayClock(restrictionsData?.serverNow)
  const wolfSiblingPublicEnabled
    = restrictionsData?.wolfSiblingPublicEnabled !== false

  useEffect(() => {
    if (wolfSiblingPublicEnabled)
      return
    setSelectedCustomRoles(prev => new Set([...prev].filter(
      key => key !== WOLF_SIBLING_PAIR_KEY,
    )))
    setRole(prev => isWolfSiblingSeatKey(prev) ? '' : prev)
    setRoleModelAssignments(prev => Object.fromEntries(Object.entries(prev)
      .filter(([key]) => !isWolfSiblingSeatKey(key))))
    setByomRoleAssignments(prev => Object.fromEntries(Object.entries(prev)
      .filter(([key]) => !isWolfSiblingSeatKey(key))))
  }, [wolfSiblingPublicEnabled])

  useEffect(() => {
    if (!accountCalendar.ready)
      return
    const refreshAnonymousBrowserUsage = () => {
      refreshQuotaClock()
      setAnonymousBrowserFreeGamesUsed(readAnonymousFreeGamesUsed(undefined, currentQuotaTime(), accountTimeZone))
    }
    const handleStorage = (event: StorageEvent) => {
      if (
        event.key === null
        || event.key === ANONYMOUS_FREE_GAME_USAGE_STORAGE_KEY
      ) {
        refreshAnonymousBrowserUsage()
      }
    }

    const nextResetAtMs = actor && restrictionsData?.nextResetAt
      ? Date.parse(restrictionsData.nextResetAt)
      : Number.NaN
    let resetTimer: number
    const scheduleReset = () => {
      const nowMs = currentQuotaTime()
      const resetAtMs = Number.isFinite(nextResetAtMs) && nextResetAtMs > nowMs
        ? nextResetAtMs
        : getNextFreeGameQuotaReset(new Date(nowMs), accountTimeZone).getTime()
      resetTimer = window.setTimeout(() => {
        refreshAnonymousBrowserUsage()
        void refetchRestrictions()
        scheduleReset()
      }, Math.max(0, resetAtMs - nowMs + 50))
    }
    const handleFocus = () => {
      refreshAnonymousBrowserUsage()
      if (Number.isFinite(nextResetAtMs) && nextResetAtMs <= currentQuotaTime())
        void refetchRestrictions()
    }

    refreshAnonymousBrowserUsage()
    scheduleReset()
    window.addEventListener('storage', handleStorage)
    window.addEventListener('focus', handleFocus)
    return () => {
      window.removeEventListener('storage', handleStorage)
      window.removeEventListener('focus', handleFocus)
      window.clearTimeout(resetTimer)
    }
  }, [accountCalendar.ready, accountTimeZone, actor, refetchRestrictions, restrictionsData?.nextResetAt, currentQuotaTime, refreshQuotaClock])

  const { data: customEndpointData } = useQuery({
    ...trpc.userRouter.getCustomEndpoint.queryOptions(),
    enabled: !!user && gameSettingForGame === 'BYOM',
  })
  const hasCustomProvider = !!(
    customEndpointData?.customApiKey && customEndpointData?.customEndpoint
  )
  const customModelLabel = customEndpointData?.customModelId || 'Custom Model'

  const { mutateAsync: validateByomEndpoint } = useMutation(
    trpc.userRouter.validateByomEndpoint.mutationOptions(),
  )

  const { data: responsiveModelsData } = useQuery({
    ...trpc.queryRouter.getResponsiveModels.queryOptions(),
    enabled:
      supportsPlatformRoleModelSelection
      || gameSettingForGame === 'MODEL_COMPARISON'
      || gameSettingForGame === 'BYOM',
  })

  const [roleModelAssignments, setRoleModelAssignments] = useState<
    Record<string, string>
  >({})

  const [comparisonModels, setComparisonModels] = useState<string[]>(['', ''])
  const [comparedRoleKey, setComparedRoleKey] = useState('')

  const [byomRoleAssignments, setByomRoleAssignments] = useState<
    Record<string, string>
  >({})

  const gamePassSummary = useGamePassSummary()
  const [fundingSource, setFundingSource] = useState<'credits' | 'game-pass' | null>(null)
  const gamePassEligible = gameSettingForGame !== 'MODEL_COMPARISON'
    && gameSettingForGame !== 'BYOM'
  const hasGamePasses = (gamePassSummary.data?.remaining ?? 0) > 0
  const selectedFundingSource = fundingSource
    ?? (gamePassSummary.data ? hasGamePasses ? 'game-pass' : 'credits' : null)
  const isFundingChoiceUnresolved = !!user && !user.isAnonymous
    && gamePassEligible && !isFreeBillingMode && selectedFundingSource === null
  const usesGamePass = gamePassEligible && !isFreeBillingMode
    && selectedFundingSource === 'game-pass'
  const canUseCredits = !!user && !!restrictionsData?.hasPurchase
  const isFreeUser = !canUseCredits
  const isUnregistered = authResolved && !user
  const canPay = canUseCredits || (gamePassEligible && (hasGamePasses || usesGamePass))
  const canSelectPaidMode = canUseCredits || (gamePassEligible && hasGamePasses) || usesGamePass

  useEffect(() => {
    if (fundingSource === null && hasGamePasses && gamePassEligible)
      setFundingSource('game-pass')
  }, [fundingSource, gamePassEligible, gamePassSummary.data, hasGamePasses])
  const effectiveFreeGamesLimit = isUnregistered
    ? FREE_GAMES_PER_WINDOW
    : (restrictionsData?.freeGamesLimit ?? 0)
  const effectiveFreeGamesUsed = isUnregistered
    ? Math.max(
        restrictionsData?.freeGamesUsed ?? 0,
        anonymousBrowserFreeGamesUsed,
      )
    : (restrictionsData?.freeGamesUsed ?? 0)
  const freeGamesRemaining = Math.max(
    0,
    effectiveFreeGamesLimit - effectiveFreeGamesUsed,
  )
  const hasFreeGames = restrictionsData?.freeGamesUnlimited === true
    || !restrictionsData || freeGamesRemaining > 0
  const isFreeModeUnavailable
    = !isMultiplayer && !!restrictionsData
      && (!!actor || isUnregistered) && !hasFreeGames
  const isPaidModeUnavailable = isUnregistered || (!!actor && !canSelectPaidMode)
  const effectiveFreeGame
    = isFreeBillingMode && (isMultiplayer || isAnonymous || hasFreeGames)
  const effectiveHighPerformance = isExpertBillingMode && canPay
  const serverQuotaResetAtMs = actor && restrictionsData?.nextResetAt
    ? Date.parse(restrictionsData.nextResetAt)
    : Number.NaN
  const quotaResetAt = Number.isFinite(serverQuotaResetAtMs)
    && serverQuotaResetAtMs > quotaServerNowMs
    ? new Date(serverQuotaResetAtMs)
    : getNextFreeGameQuotaReset(new Date(quotaServerNowMs), accountTimeZone)
  const quotaResetLabel = Number.isNaN(quotaResetAt.getTime())
    ? ''
    : quotaResetAt.toLocaleString(language === 'tw' ? 'zh-TW' : language, {
        dateStyle: 'medium',
        timeStyle: 'short',
        timeZone: accountTimeZone,
      })
  const freeGameAvailableCopy = t('game.freeGameAvailable').replace(
    '{count}',
    String(freeGamesRemaining),
  )

  useEffect(() => {
    if (!user || !restrictionsData || gamePassSummary.isPending || gamePassSummary.isError)
      return

    if (billingMode !== 'free' && !canPay && !hasGamePasses) {
      setBillingMode('free')
      return
    }

    if (!isMultiplayer && billingMode === 'free' && canPay && !hasFreeGames) {
      setBillingMode('quick')
    }
  }, [billingMode, canPay, gamePassSummary.isPending, gamePassSummary.isError, hasFreeGames, hasGamePasses, isMultiplayer, restrictionsData, user])

  useEffect(() => {
    try {
      localStorage.setItem(LAST_BILLING_MODE_STORAGE_KEY, billingMode)
    }
    catch {}
  }, [billingMode])

  useEffect(() => {
    try {
      localStorage.setItem('lastGameSetting', gameSettingForGame)
    }
    catch {}
  }, [gameSettingForGame])

  const availableModels = useMemo(() => {
    if (!responsiveModelsData?.data)
      return [] as string[]
    const lc = responsiveModelsData.data.lowCost || []
    const hp = responsiveModelsData.data.highPerformance || []
    const preferred = effectiveHighPerformance
      ? hp.length > 0
        ? hp
        : lc
      : lc.length > 0
        ? lc
        : hp
    return [...new Set(preferred)]
  }, [responsiveModelsData, effectiveHighPerformance])
  const availableModelPoolSignature
    = getResponsiveModelPoolSignature(availableModels)
  const comparisonModelOptions = useMemo(
    () => [
      ...availableModels.filter(model => !isJevComparisonModel(model)),
      JEV_COMPARISON_MODEL,
    ],
    [availableModels],
  )
  const hasValidComparisonModels
    = comparisonModels.every(model => comparisonModelOptions.includes(model))
      && comparisonModels.some(model => !isJevComparisonModel(model))
  const previousPlatformRerollContext = useRef('')

  const modelSeatDescriptors = useMemo(() => {
    const config = getRoleConfiguration(gameSettingForGame)
    return buildModelSeatDescriptors({
      gameSetting: gameSettingForGame,
      allGoodRoleKeys: config.goodRoles.map(roleConfig => roleConfig.key),
      allEvilRoleKeys: config.evilRoles
        .filter(roleConfig => roleConfig.key !== WOLF_SIBLING_PAIR_KEY
          || wolfSiblingPublicEnabled)
        .map(roleConfig => roleConfig.key),
      fixedVillagerCount: config.villagerCount,
      selectedRoleKeys: [...selectedCustomRoles],
      selectedVillagerIndices: [...selectedCustomVillagers],
    })
  }, [gameSettingForGame, selectedCustomRoles, selectedCustomVillagers, wolfSiblingPublicEnabled])
  const platformModelSeats = useMemo(
    () => supportsPlatformRoleModelSelection ? modelSeatDescriptors : [],
    [modelSeatDescriptors, supportsPlatformRoleModelSelection],
  )
  const runtimeSeatKeyBySource = useMemo(
    () =>
      new Map(
        modelSeatDescriptors.map(seat => [seat.sourceKey, seat.runtimeKey]),
      ),
    [modelSeatDescriptors],
  )
  const normalizedRoleModelAssignments = useMemo(
    () => normalizeSeatAssignments(roleModelAssignments, modelSeatDescriptors),
    [modelSeatDescriptors, roleModelAssignments],
  )
  const normalizedByomRoleAssignments = useMemo(
    () => normalizeSeatAssignments(byomRoleAssignments, modelSeatDescriptors),
    [byomRoleAssignments, modelSeatDescriptors],
  )
  const comparedRuntimeRoleKey = comparedRoleKey
    ? (runtimeSeatKeyBySource.get(comparedRoleKey) ?? '')
    : ''
  const runtimeEvilSeatKeys = useMemo(() => {
    const evilSourceKeys = new Set(
      getRoleConfiguration(gameSettingForGame).evilRoles.filter(evilRole => evilRole.key !== WOLF_SIBLING_PAIR_KEY
        || wolfSiblingPublicEnabled).flatMap(
        evilRole => expandWolfSiblingChoice(evilRole.key),
      ),
    )
    return modelSeatDescriptors
      .filter(seat => evilSourceKeys.has(seat.sourceKey))
      .map(seat => seat.runtimeKey)
  }, [gameSettingForGame, modelSeatDescriptors, wolfSiblingPublicEnabled])

  useEffect(() => {
    if (gameSettingForGame !== 'MODEL_COMPARISON') {
      return
    }
    setComparisonModels((current) => {
      const pool = availableModelPoolSignature
        ? availableModelPoolSignature.split('\u0000').filter(model => !isJevComparisonModel(model))
        : []
      const sampled = sampleComparisonModels(pool)
      const isAvailable = (model: string) =>
        model === JEV_COMPARISON_MODEL || pool.includes(model)
      return current.every(model => isAvailable(model))
        ? current
        : current.map((model, index) => isAvailable(model) ? model : sampled[index]!)
    })
  }, [availableModelPoolSignature, gameSettingForGame])

  useEffect(() => {
    if (!usesCustomRosterTemplate(gameSettingForGame) || !isConfigLoaded)
      return
    const playerCount = isFreeBillingMode ? 10 : customPlayerCount
    if (playerCount !== customPlayerCount)
      setCustomPlayerCount(playerCount)
    setSelectedCustomRoles((current) => {
      const choices = customRosterSelections(current)
      const next = customRosterSelectionKeys(playerCount, choices.extraRole, choices.wolfChoice)
      return next.size === current.size && [...next].every(key => current.has(key)) ? current : next
    })
    setSelectedCustomVillagers((current) => {
      const next = customRosterVillagerKeys(playerCount)
      return next.size === current.size && [...next].every(key => current.has(key)) ? current : next
    })
  }, [customPlayerCount, gameSettingForGame, isConfigLoaded, isFreeBillingMode])

  const getModelDisplayName = (fullModelName: string) => {
    const parts = fullModelName.split('/')
    return parts.length > 1 ? parts.slice(1).join('/') : fullModelName
  }

  useEffect(() => {
    const savedConfig = getStoredGameConfiguration(gameSettingForGame)
    const restoredSheriff
      = typeof savedConfig?.hasSheriff === 'boolean'
        ? savedConfig.hasSheriff
        : legacySheriffPreference.current.setting === gameSettingForGame
          ? legacySheriffPreference.current.hasSheriff
          : false
    setHasSheriff(restoredSheriff)
    const settingCategory = isVariableSeatBoard(gameSettingForGame)
      ? 'VARIABLE_SEAT_BOARD'
      : gameSettingForGame
    switch (settingCategory) {
      case 'G10_ULTIMATE_TRIAL':
      case 'G12_ULTIMATE_TRIAL': {
        setRoleModelAssignments(savedConfig?.roleModelAssignments ?? {})
        setRole('')
        setSelectedD10Roles(new Set())
        setSelectedD10Villagers(new Set([0, 1, 2]))
        setIsConfigLoaded(true)
        break
      }
      case 'USER_CUSTOM_CONFIG':
      case 'HUMAN_VS_AI':
      case 'WATCH_MODE':
      case 'MODEL_COMPARISON':
      case 'BYOM': {
        const legacyPlayerCount = savedConfig?.roles && savedConfig.villagers
          ? countRoleSeats(new Set(savedConfig.roles)) + new Set(savedConfig.villagers).size
          : undefined
        const playerCount = savedConfig?.playerCount
          ?? (legacyPlayerCount === 12 ? 12 : 10)
        const count = isFreeBillingMode ? 10 : playerCount
        const choices = savedConfig?.roles
          ? customRosterSelections(new Set(savedConfig.roles))
          : { extraRole: 'knight', wolfChoice: 'alpha_wolf' }
        const wolfChoice = (savedConfig?.roles?.some(key => isRoleDisabledForHumanVsAi(gameSettingForGame, key))
          || (!wolfSiblingPublicEnabled && choices.wolfChoice === WOLF_SIBLING_PAIR_KEY))
          ? undefined
          : choices.wolfChoice
        setRoleModelAssignments(savedConfig?.roleModelAssignments ?? {})
        if (gameSettingForGame === 'BYOM')
          setByomRoleAssignments(savedConfig?.byomRoleAssignments ?? {})
        setCustomPlayerCount(count)
        setSelectedCustomRoles(customRosterSelectionKeys(count, choices.extraRole, wolfChoice))
        setSelectedCustomVillagers(customRosterVillagerKeys(count))
        setRole(savedConfig?.yourRole ?? '')
        setIsConfigLoaded(true)
        break
      }
      case 'G6_1SR1WT_2WW_2VG':
      case 'G9_1HT1SR1WT_3WW_3VG':
      case 'VARIABLE_SEAT_BOARD': {
        let loaded = false
        const isCustomMode
          = gameSettingForGame === 'USER_CUSTOM_CONFIG'
            || gameSettingForGame === 'HUMAN_VS_AI'
            || gameSettingForGame === 'WATCH_MODE'
            || gameSettingForGame === 'MODEL_COMPARISON'
            || gameSettingForGame === 'BYOM'

        if (savedConfig) {
          if (typeof savedConfig.yourRole === 'string') {
            setRole(
              isRoleDisabledForHumanVsAi(gameSettingForGame, savedConfig.yourRole)
              || (!wolfSiblingPublicEnabled
                && isWolfSiblingSeatKey(savedConfig.yourRole))
                ? ''
                : savedConfig.yourRole,
            )
          }
          if (
            isCustomMode
            && savedConfig.roles !== undefined
            && savedConfig.villagers !== undefined
          ) {
            setSelectedCustomRoles(new Set(savedConfig.roles.filter(
              key => !isRoleDisabledForHumanVsAi(gameSettingForGame, key)
                && (key !== WOLF_SIBLING_PAIR_KEY || wolfSiblingPublicEnabled),
            )))
            setSelectedCustomVillagers(new Set(savedConfig.villagers))
            loaded = true
          }
          if (
            gameSettingForGame === 'BYOM'
            && savedConfig.byomRoleAssignments
          ) {
            setByomRoleAssignments(Object.fromEntries(
              Object.entries(savedConfig.byomRoleAssignments)
                .filter(([key]) => wolfSiblingPublicEnabled
                  || !isWolfSiblingSeatKey(key)),
            ))
          }
        }

        if (
          !loaded
          && (gameSettingForGame === 'USER_CUSTOM_CONFIG'
            || gameSettingForGame === 'HUMAN_VS_AI'
            || gameSettingForGame === 'WATCH_MODE'
            || gameSettingForGame === 'MODEL_COMPARISON'
            || gameSettingForGame === 'BYOM')
        ) {
          const config = getRoleConfiguration(gameSettingForGame)

          const selectedEvilRoles = config.evilRoles
            .filter(r => r.key !== 'gargoyle' && r.enabled)
            .map(r => r.key)
            .slice(0, 3)

          const selectedGoodRoles = config.goodRoles.map(r => r.key).slice(0, 4)

          const rolesSet = new Set([...selectedEvilRoles, ...selectedGoodRoles])
          const villagersSet = new Set([0, 1, 2])

          setSelectedCustomRoles(rolesSet)
          setSelectedCustomVillagers(villagersSet)

          setRole('')
        }
        else if (
          !loaded
          && (gameSettingForGame === 'G6_1SR1WT_2WW_2VG'
            || gameSettingForGame === 'G9_1HT1SR1WT_3WW_3VG'
            || isFixedGameSetting(gameSettingForGame))
        ) {
          setSelectedCustomRoles(new Set())
          setSelectedCustomVillagers(
            new Set(
              Array.from(
                {
                  length:
                    getRoleConfiguration(gameSettingForGame).villagerCount,
                },
                (_, index) => index,
              ),
            ),
          )
          if (!savedConfig?.yourRole) {
            setRole('werewolf')
          }
        }

        setIsConfigLoaded(true)
        break
      }
      default: {
        setRole('')
        setSelectedCustomRoles(new Set())
        setSelectedCustomVillagers(new Set([0, 1, 2, 3, 4, 5, 6, 7]))
        setIsConfigLoaded(true)
      }
    }
    if (isFreeUser && gameSettingForGame === 'BYOM' && !user) {
      setValidationError(t('game.error.login_required_for_mode'))
    }
    else {
      setValidationError('')
    }
  }, [gameSettingForGame, t, isFreeUser, user, wolfSiblingPublicEnabled])

  useEffect(() => {
    if (
      gameSettingForGame === 'USER_CUSTOM_CONFIG'
      || gameSettingForGame === 'HUMAN_VS_AI'
      || gameSettingForGame === 'WATCH_MODE'
      || gameSettingForGame === 'MODEL_COMPARISON'
      || gameSettingForGame === 'BYOM'
    ) {
      if (isFreeUser && gameSettingForGame === 'BYOM' && !user) {
        setValidationError(t('game.error.login_required_for_mode'))
      }
      else if (usesCustomRosterTemplate(gameSettingForGame) && isConfigLoaded
        && (!customExtraRole || !customWolfChoice)) {
        setValidationError(t('game.invalidRoleConfiguration'))
      }
      else {
        setValidationError('')
      }
    }
  }, [
    customExtraRole,
    customWolfChoice,
    isConfigLoaded,
    selectedCustomRoles,
    selectedCustomVillagers,
    gameSettingForGame,
    isFreeUser,
    t,
    user,
  ])

  useEffect(() => {
    if (!isConfigLoaded)
      return
    const rerollContext = getRoleModelRerollContext(
      gameSettingForGame,
      availableModelPoolSignature,
      supportsPlatformRoleModelSelection,
    )
    if (!rerollContext) {
      previousPlatformRerollContext.current = ''
      return
    }
    const contextChanged
      = previousPlatformRerollContext.current !== rerollContext
    previousPlatformRerollContext.current = rerollContext
    setRoleModelAssignments((current) => {
      const next = reconcileRoleModelAssignments(
        platformModelSeats.map(seat => seat.sourceKey),
        availableModelPoolSignature.split('\u0000'),
        contextChanged
          ? (usesCustomRosterTemplate(gameSettingForGame) || (readSettingMode(gameSettingForGame).symbolicPlayerCount !== null))
              ? getStoredGameConfiguration(gameSettingForGame)?.roleModelAssignments ?? {}
              : {}
          : current,
      )
      return sameRoleModelAssignments(current, next) ? current : next
    })
  }, [
    availableModelPoolSignature,
    gameSettingForGame,
    platformModelSeats,
    isConfigLoaded,
    supportsPlatformRoleModelSelection,
  ])

  useEffect(() => {
    if (!isConfigLoaded || gameSettingForGame !== 'BYOM' || !responsiveModelsData?.data || !customEndpointData)
      return
    const defaultModel = hasCustomProvider ? 'custom' : availableModels[0]
    setByomRoleAssignments((current) => {
      const next: Record<string, string> = {}
      for (const { sourceKey } of modelSeatDescriptors) {
        const existing = current[sourceKey]
        const model = existing && (existing === 'custom' || availableModels.includes(existing))
          ? existing : defaultModel
        if (model)
          next[sourceKey] = model
      }
      return sameRoleModelAssignments(current, next) ? current : next
    })
  }, [gameSettingForGame, isConfigLoaded, modelSeatDescriptors, availableModels, hasCustomProvider, responsiveModelsData, customEndpointData])

  useEffect(() => {
    if (
      gameSettingForGame !== 'USER_CUSTOM_CONFIG'
      && gameSettingForGame !== 'HUMAN_VS_AI'
      && gameSettingForGame !== 'WATCH_MODE'
      && gameSettingForGame !== 'MODEL_COMPARISON'
      && gameSettingForGame !== 'BYOM'
    ) {
      setRoleCodeString('')
      return
    }

    if (!isConfigLoaded) {
      return
    }

    if (selectedCustomRoles.size === 0 && selectedCustomVillagers.size === 0) {
      setRoleCodeString('')
      return
    }

    const villagerCount = selectedCustomVillagers.size
    const selectedRoles = roleCountsForChoices(selectedCustomRoles)

    try {
      const choices = customRosterSelections(selectedCustomRoles)
      let codeString = usesCustomRosterTemplate(gameSettingForGame)
        ? buildCustomRosterSetting(customPlayerCount, choices.extraRole ?? '', choices.wolfChoice ?? '')
        : generateRoleCodeStringFromCheckbox('game', selectedRoles, villagerCount)
      if (
        gameSettingForGame === 'WATCH_MODE'
        || gameSettingForGame === 'MODEL_COMPARISON'
        || gameSettingForGame === 'BYOM'
      ) {
        switch (gameSettingForGame) {
          case 'WATCH_MODE': {
            codeString = writeSetting({ kind: 'select-mode', setting: codeString, mode: 'W' })
            break
          }
          case 'MODEL_COMPARISON': {
            codeString = writeSetting({ kind: 'select-mode', setting: codeString, mode: 'C' })
            break
          }
          case 'BYOM': {
            codeString = writeSetting({ kind: 'select-mode', setting: codeString, mode: 'B' })
            break
          }
          default: {
            break
          }
        }
      }
      setRoleCodeString(codeString)
      console.log('🎯 Checkbox Updated - GameSetting:', codeString)

      const availableSpecialRoles = Array.from(selectedCustomRoles)
        .filter(roleKey =>
          !isRoleDisabledForHumanVsAi(gameSettingForGame, roleKey))
        .flatMap(roleKey => expandWolfSiblingChoice(roleKey))

      const availableRoles = [...availableSpecialRoles]
      if (selectedCustomVillagers.size > 0) {
        availableRoles.push('villager')
      }

      const normalizedRole = [
        'werewolf_2',
        'werewolf_3',
        'werewolf_4',
      ].includes(role)
        ? 'werewolf'
        : role
      const isCurrentRoleAvailable
        = role
          && (availableSpecialRoles.some(
            r =>
              (['werewolf_2', 'werewolf_3', 'werewolf_4'].includes(r)
                ? 'werewolf'
                : r) === normalizedRole,
          )
          || (role === 'villager' && selectedCustomVillagers.size > 0))

      if (!role || !isCurrentRoleAvailable) {
        if (availableRoles.length === 0) {
          setRole('')
          console.log('🎭 Cleared Your Role: No roles available')
          return
        }

        const firstRole = ['werewolf_2', 'werewolf_3', 'werewolf_4'].includes(
          availableRoles[0]!,
        )
          ? 'werewolf'
          : availableRoles[0]!
        setRole(firstRole)
        console.log(
          '🎭 Auto-selected Your Role to first selection:',
          t(`game.${firstRole}`),
        )
      }
    }
    catch {
      setRoleCodeString('')
    }
  }, [
    selectedCustomRoles,
    selectedCustomVillagers,
    customPlayerCount,
    gameSettingForGame,
    role,
    t,
    isConfigLoaded,
  ])

  const handleGameSettingChange = useCallback((
    value: string,
    targetBillingMode: BillingMode = billingMode,
    targetPlayMode: PlayMode = playMode,
  ) => {
    const nextSetting = targetBillingMode === 'free'
      ? tenSeatSettingFor(value)
      : value
    if (!wolfSiblingPublicEnabled && isWolfSiblingBoard(nextSetting)) {
      return
    }
    if (isUnregistered && isVariableSeatBoard(nextSetting)) {
      return
    }
    if (
      targetBillingMode === 'free'
      && !readRoster({ kind: 'selection', setting: nextSetting, playMode: targetPlayMode }).freeGameEligible
    ) {
      return
    }

    const tab = SETTING_TO_TAB[nextSetting]
    if (tab) {
      setSearchParams((current) => {
        const next = new URLSearchParams(current)
        next.set('tab', tab)
        return next
      }, { replace: true })
    }
    if (nextSetting === gameSettingForGame)
      return

    setGameSettingForGame(nextSetting)
    setIsConfigLoaded(false)
    if (!(readSettingMode(nextSetting).symbolicPlayerCount !== null)) {
      setSelectedD10Roles(new Set())
      setSelectedD10Villagers(new Set([0, 1, 2]))
    }
    if (
      nextSetting !== 'USER_CUSTOM_CONFIG'
      && nextSetting !== 'HUMAN_VS_AI'
      && nextSetting !== 'WATCH_MODE'
      && nextSetting !== 'MODEL_COMPARISON'
      && nextSetting !== 'BYOM'
    ) {
      setSelectedCustomRoles(new Set())
      setSelectedCustomVillagers(new Set([0, 1, 2, 3, 4, 5, 6, 7]))
    }
    setValidationError('')
  }, [billingMode, gameSettingForGame, isUnregistered, playMode, setSearchParams, wolfSiblingPublicEnabled])

  useEffect(() => {
    if (!wolfSiblingPublicEnabled && isWolfSiblingBoard(gameSettingForGame)) {
      handleGameSettingChange(DEFAULT_GAME_SETTING)
      return
    }
    if (isUnregistered && isVariableSeatBoard(gameSettingForGame)) {
      handleGameSettingChange(DEFAULT_GAME_SETTING)
      return
    }
    if (!isFreeBillingMode)
      return

    const freeSetting = tenSeatSettingFor(gameSettingForGame)
    if (freeSetting !== gameSettingForGame) {
      handleGameSettingChange(freeSetting)
    }
    else if (!readRoster({ kind: 'selection', setting: gameSettingForGame, playMode }).freeGameEligible) {
      handleGameSettingChange(DEFAULT_GAME_SETTING)
    }
  }, [gameSettingForGame, handleGameSettingChange, isFreeBillingMode, isUnregistered, playMode, wolfSiblingPublicEnabled])

  useEffect(() => {
    if (!isConfigLoaded)
      return

    try {
      const storageKey = `${GAME_CONFIG_STORAGE_PREFIX}${(readSettingMode(gameSettingForGame).symbolicPlayerCount !== null) ? GameSettingOptions.G10_ULTIMATE_TRIAL : gameSettingForGame}`
      const raw = localStorage.getItem(storageKey)
      const parsed: unknown = raw ? JSON.parse(raw) : null
      const existing
        = parsed && typeof parsed === 'object' && !Array.isArray(parsed)
          ? parsed
          : {}
      const isCustomMode
        = gameSettingForGame === 'USER_CUSTOM_CONFIG'
          || gameSettingForGame === 'HUMAN_VS_AI'
          || gameSettingForGame === 'WATCH_MODE'
          || gameSettingForGame === 'MODEL_COMPARISON'
          || gameSettingForGame === 'BYOM'
      const roles = isCustomMode
        ? Array.from(selectedCustomRoles)
        : Array.from(selectedD10Roles)
      const villagers = isCustomMode
        ? Array.from(selectedCustomVillagers)
        : Array.from(selectedD10Villagers)
      const configToSave: Record<string, unknown> = {
        ...existing,
        ...(usesCustomRosterTemplate(gameSettingForGame) ? { playerCount: customPlayerCount, roleModelAssignments } : {}),
        ...((readSettingMode(gameSettingForGame).symbolicPlayerCount !== null) ? { playerCount: readSettingMode(gameSettingForGame).symbolicPlayerCount, roleModelAssignments } : {}),
        roles,
        villagers,
        yourRole: role,
        hasSheriff,
      }
      if (gameSettingForGame === 'BYOM') {
        configToSave.byomRoleAssignments = byomRoleAssignments
      }
      localStorage.setItem(storageKey, JSON.stringify(configToSave))
      localStorage.setItem(HAS_SHERIFF_STORAGE_KEY, String(hasSheriff))
    }
    catch {}
  }, [
    byomRoleAssignments,
    customPlayerCount,
    gameSettingForGame,
    roleModelAssignments,
    hasSheriff,
    isConfigLoaded,
    role,
    selectedCustomRoles,
    selectedCustomVillagers,
    selectedD10Roles,
    selectedD10Villagers,
  ])

  const handleRoleToggle = (roleKey: string, isGood: boolean) => {
    if (isRoleDisabledForHumanVsAi(gameSettingForGame, roleKey))
      return
    if (usesCustomRosterTemplate(gameSettingForGame)) {
      setSelectedCustomRoles((current) => {
        const choices = customRosterSelections(current)
        const nextChoice = (isGood ? choices.extraRole : choices.wolfChoice) === roleKey ? undefined : roleKey
        return customRosterSelectionKeys(customPlayerCount, isGood ? nextChoice : choices.extraRole, isGood ? choices.wolfChoice : nextChoice)
      })
      setValidationError('')
      return
    }
    if ((readSettingMode(gameSettingForGame).symbolicPlayerCount !== null)) {
      setSelectedD10Roles((prev) => {
        const newSet = new Set(prev)
        if (newSet.has(roleKey)) {
          newSet.delete(roleKey)
        }
        else {
          newSet.add(roleKey)
        }
        return newSet
      })
    }
    else if (
      gameSettingForGame === 'USER_CUSTOM_CONFIG'
      || gameSettingForGame === 'HUMAN_VS_AI'
      || gameSettingForGame === 'WATCH_MODE'
      || gameSettingForGame === 'MODEL_COMPARISON'
      || gameSettingForGame === 'BYOM'
    ) {
      setSelectedCustomRoles((prev) => {
        const newSet = new Set(prev)
        if (newSet.has(roleKey)) {
          newSet.delete(roleKey)
          if (supportsPlatformRoleModelSelection) {
            setRoleModelAssignments((prevAssign) => {
              const updated = { ...prevAssign }
              for (const seatRole of expandWolfSiblingChoice(roleKey))
                delete updated[seatRole]
              return updated
            })
          }
          if (gameSettingForGame === 'BYOM') {
            setByomRoleAssignments((prevAssign) => {
              const updated = { ...prevAssign }
              for (const seatRole of expandWolfSiblingChoice(roleKey))
                delete updated[seatRole]
              return updated
            })
          }
        }
        else {
          newSet.add(roleKey)
          if (supportsPlatformRoleModelSelection) {
            const model = pickRandom(availableModels)
            if (model) {
              setRoleModelAssignments(prevAssign => ({
                ...prevAssign,
                ...Object.fromEntries(expandWolfSiblingChoice(roleKey)
                  .map(seatRole => [seatRole, model])),
              }))
            }
          }
          if (gameSettingForGame === 'BYOM') {
            const model = pickRandom(availableModels)
            if (model) {
              setByomRoleAssignments(prevAssign => ({
                ...prevAssign,
                ...Object.fromEntries(expandWolfSiblingChoice(roleKey)
                  .map(seatRole => [seatRole, model])),
              }))
            }
          }
        }
        return newSet
      })
    }
  }

  const isRoleEnabled = (roleKey: string, defaultEnabled: boolean): boolean => {
    if ((readSettingMode(gameSettingForGame).symbolicPlayerCount !== null)) {
      return selectedD10Roles.has(roleKey)
    }
    else if (
      gameSettingForGame === 'USER_CUSTOM_CONFIG'
      || gameSettingForGame === 'HUMAN_VS_AI'
      || gameSettingForGame === 'WATCH_MODE'
      || gameSettingForGame === 'MODEL_COMPARISON'
      || gameSettingForGame === 'BYOM'
    ) {
      return selectedCustomRoles.has(roleKey)
    }
    return defaultEnabled
  }

  const isCheckboxDisabled = (roleKey?: string): boolean => {
    if (roleKey && isRoleDisabledForHumanVsAi(gameSettingForGame, roleKey)) {
      return true
    }
    return (
      gameSettingForGame !== 'USER_CUSTOM_CONFIG'
      && gameSettingForGame !== 'HUMAN_VS_AI'
      && gameSettingForGame !== 'WATCH_MODE'
      && gameSettingForGame !== 'MODEL_COMPARISON'
      && gameSettingForGame !== 'BYOM'
    )
  }

  const getVillagerCount = (): number => {
    if ((readSettingMode(gameSettingForGame).symbolicPlayerCount !== null)) {
      return selectedD10Villagers.size
    }
    else if (
      gameSettingForGame === 'USER_CUSTOM_CONFIG'
      || gameSettingForGame === 'HUMAN_VS_AI'
      || gameSettingForGame === 'WATCH_MODE'
      || gameSettingForGame === 'MODEL_COMPARISON'
      || gameSettingForGame === 'BYOM'
    ) {
      return selectedCustomVillagers.size
    }
    return getRoleConfiguration(gameSettingForGame).villagerCount
  }

  const handleVillagerToggle = (index: number) => {
    if ((readSettingMode(gameSettingForGame).symbolicPlayerCount !== null)) {
      setSelectedD10Villagers((prev) => {
        const newSet = new Set(prev)
        if (newSet.has(index)) {
          newSet.delete(index)
        }
        else {
          newSet.add(index)
        }
        return newSet
      })
    }
    else if (
      gameSettingForGame === 'USER_CUSTOM_CONFIG'
      || gameSettingForGame === 'HUMAN_VS_AI'
      || gameSettingForGame === 'WATCH_MODE'
      || gameSettingForGame === 'MODEL_COMPARISON'
      || gameSettingForGame === 'BYOM'
    ) {
      setSelectedCustomVillagers((prev) => {
        const newSet = new Set(prev)
        const villagerKey = `villager_${index}`
        if (newSet.has(index)) {
          newSet.delete(index)
          if (supportsPlatformRoleModelSelection) {
            setRoleModelAssignments((prevAssign) => {
              const updated = { ...prevAssign }
              delete updated[villagerKey]
              return updated
            })
          }
          if (gameSettingForGame === 'BYOM') {
            setByomRoleAssignments((prevAssign) => {
              const updated = { ...prevAssign }
              delete updated[villagerKey]
              return updated
            })
          }
        }
        else {
          newSet.add(index)
          if (supportsPlatformRoleModelSelection) {
            const model = pickRandom(availableModels)
            if (model) {
              setRoleModelAssignments(prevAssign => ({
                ...prevAssign,
                [villagerKey]: model,
              }))
            }
          }
          if (gameSettingForGame === 'BYOM') {
            const model = pickRandom(availableModels)
            if (model) {
              setByomRoleAssignments(prevAssign => ({
                ...prevAssign,
                [villagerKey]: model,
              }))
            }
          }
        }
        return newSet
      })
    }
  }

  const handleRoleModelChange = (roleKey: string, model: string) => {
    setRoleModelAssignments(prev => ({
      ...prev,
      [roleKey]: model,
    }))
  }

  const handleRoleChange = (value: string) => {
    setRole(value)
  }

  const isVillagerCheckboxDisabled = (): boolean => {
    return (
      gameSettingForGame !== 'USER_CUSTOM_CONFIG'
      && gameSettingForGame !== 'HUMAN_VS_AI'
      && gameSettingForGame !== 'WATCH_MODE'
      && gameSettingForGame !== 'MODEL_COMPARISON'
      && gameSettingForGame !== 'BYOM'
    )
  }

  const getGameSettingLabel = (gameSettingValue: string) => {
    return t(`gameSettings.${(readSettingMode(gameSettingValue).symbolicPlayerCount !== null) ? GameSettingOptions.G10_ULTIMATE_TRIAL : gameSettingValue}`) || gameSettingValue
  }

  const handleUnselectAll = () => {
    if (usesCustomRosterTemplate(gameSettingForGame)) {
      setSelectedCustomRoles(customRosterSelectionKeys(customPlayerCount))
      setSelectedCustomVillagers(customRosterVillagerKeys(customPlayerCount))
      return
    }
    if (
      gameSettingForGame === 'USER_CUSTOM_CONFIG'
      || gameSettingForGame === 'HUMAN_VS_AI'
      || gameSettingForGame === 'WATCH_MODE'
      || gameSettingForGame === 'MODEL_COMPARISON'
      || gameSettingForGame === 'BYOM'
    ) {
      const newRolesSet = new Set<string>()
      const newVillagersSet = new Set<number>()

      setSelectedCustomRoles(newRolesSet)
      setSelectedCustomVillagers(newVillagersSet)
      setRole('')

      if (supportsPlatformRoleModelSelection) {
        setRoleModelAssignments({})
      }
      if (gameSettingForGame === 'BYOM') {
        setByomRoleAssignments({})
      }
    }
  }

  const gameSettings = Object.values(GameSettingOptions)
    .filter(gameSetting => gameSetting !== GameSettingOptions.G12_ULTIMATE_TRIAL)
    .filter(gameSetting => !gameSetting.includes('SNOW_WOLF_ALPHA'))
    .filter(gameSetting => !(
      gameSetting in TWELVE_SEAT_BOARD_SETTINGS
    ))
    .map(gameSetting => ({
      value: gameSetting,
      label: getGameSettingLabel(gameSetting),
    }))
  const freeBoardOrder = new Map<string, number>(
    FREE_GAME_SETTINGS_BY_PLAY_MODE[playMode].map((setting, index) => [setting, index]),
  )
  const isFreeEligibleBoard = (gameSetting: string) =>
    readRoster({ kind: 'selection', setting: tenSeatSettingFor(gameSetting), playMode }).freeGameEligible
  const orderedGameSettings = [...gameSettings].sort((left, right) =>
    (freeBoardOrder.get(tenSeatSettingFor(left.value)) ?? gameSettings.length)
    - (freeBoardOrder.get(tenSeatSettingFor(right.value)) ?? gameSettings.length))
  const visibleGameSettings = orderedGameSettings.filter(
    setting => (wolfSiblingPublicEnabled || !isWolfSiblingBoard(setting.value))
      && (!isMultiplayer || MULTIPLAYER_SETTINGS.has(setting.value)),
  )
  const isGameSettingLocked = (gameSetting: string) =>
    (isUnregistered && isVariableSeatBoard(gameSetting))
    || (isFreeBillingMode && !isFreeEligibleBoard(gameSetting))
  const availableGameSettings = visibleGameSettings.filter(
    setting => !isGameSettingLocked(setting.value),
  )
  const lockedGameSettings = visibleGameSettings.filter(
    setting => isGameSettingLocked(setting.value),
  )
  const renderGameSettingTab = (setting: { value: string, label: string }) => {
    const isActive
      = boardTabSettingFor(gameSettingForGame) === setting.value
    const label = getGameSettingLabel(isActive
      ? gameSettingForGame
      : isFreeBillingMode ? tenSeatSettingFor(setting.value) : setting.value)
    const isLoginLocked
      = isUnregistered && isVariableSeatBoard(setting.value)
    const isLocked = isGameSettingLocked(setting.value)
    const lockReasonKey = isLoginLocked
      ? 'game.error.login_required_for_mode'
      : 'game.error.free_game_mode_not_allowed'

    return (
      <button
        type="button"
        key={setting.value}
        data-testid={`mode-tab-${setting.value}`}
        aria-pressed={isActive}
        aria-disabled={isLocked || undefined}
        aria-label={isLocked
          ? `${label}: ${t(lockReasonKey)}`
          : undefined}
        title={isLocked
          ? t(lockReasonKey)
          : undefined}
        onClick={() => handleGameSettingChange(
          isActive ? gameSettingForGame : setting.value,
        )}
        className={`inline-flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
          isLocked
            ? 'cursor-not-allowed border-border bg-muted/60 text-muted-foreground opacity-70'
            : isActive
            ? 'border-primary bg-primary/10 text-primary shadow-sm'
            : 'border-border bg-background text-muted-foreground hover:border-primary/40 hover:bg-muted/50 hover:text-foreground'
        }`}
      >
        {isLocked && (
          <Lock className="size-3 shrink-0" aria-hidden="true" />
        )}
        {label}
      </button>
    )
  }

  const currentGameSetting = gameSettings.find(
    gs => gs.value === gameSettingForGame,
  )

  const roleOptions = useMemo(() => {
    const options = new Set<string>()

    if (
      gameSettingForGame === 'USER_CUSTOM_CONFIG'
      || gameSettingForGame === 'HUMAN_VS_AI'
      || gameSettingForGame === 'WATCH_MODE'
      || gameSettingForGame === 'MODEL_COMPARISON'
      || gameSettingForGame === 'BYOM'
    ) {
      selectedCustomRoles.forEach((roleKey) => {
        if (isRoleDisabledForHumanVsAi(gameSettingForGame, roleKey))
          return
        for (const seatRole of expandWolfSiblingChoice(roleKey)) {
          const canonicalRoleKey = [
            'werewolf_2',
            'werewolf_3',
            'werewolf_4',
          ].includes(seatRole)
            ? 'werewolf'
            : seatRole
          options.add(canonicalRoleKey)
        }
      })

      if (selectedCustomVillagers.size > 0) {
        options.add('villager')
      }
    }
    else {
      const config = getRoleConfiguration(gameSettingForGame)

      config.goodRoles.forEach((role) => {
        if (role.enabled) {
          options.add(role.key)
        }
      })

      config.evilRoles.forEach((role) => {
        if (role.enabled) {
          const canonicalRoleKey = [
            'werewolf_2',
            'werewolf_3',
            'werewolf_4',
          ].includes(role.key)
            ? 'werewolf'
            : role.key
          options.add(canonicalRoleKey)
        }
      })

      if (config.villagerCount > 0) {
        options.add('villager')
      }
    }

    return Array.from(options).sort()
  }, [gameSettingForGame, selectedCustomRoles, selectedCustomVillagers])

  const comparisonRoleOptions = useMemo(() => {
    if (gameSettingForGame !== 'MODEL_COMPARISON')
      return []
    return modelSeatDescriptors
  }, [gameSettingForGame, modelSeatDescriptors])

  useEffect(() => {
    if (
      gameSettingForGame === 'MODEL_COMPARISON'
      && comparedRoleKey
      && !comparisonRoleOptions.some(seat => seat.sourceKey === comparedRoleKey)
    ) {
      setComparedRoleKey('')
    }
  }, [comparedRoleKey, comparisonRoleOptions, gameSettingForGame])

  const isCustomConfigDisabled = useMemo(() => {
    if (usesCustomRosterTemplate(gameSettingForGame) && (!customExtraRole || !customWolfChoice))
      return true
    if ([...selectedCustomRoles].some(roleKey =>
      isRoleDisabledForHumanVsAi(gameSettingForGame, roleKey))) {
      return true
    }
    if (
      gameSettingForGame !== 'USER_CUSTOM_CONFIG'
      && gameSettingForGame !== 'HUMAN_VS_AI'
      && gameSettingForGame !== 'WATCH_MODE'
      && gameSettingForGame !== 'MODEL_COMPARISON'
    ) {
      return false
    }

    const werewolfRoles = Array.from(selectedCustomRoles).filter(r =>
      getRoleConfiguration(gameSettingForGame).evilRoles.some(
        er => er.key === r,
      ),
    )
    const specialGoodRoles = Array.from(selectedCustomRoles).filter(r =>
      getRoleConfiguration(gameSettingForGame).goodRoles.some(
        gr => gr.key === r,
      ),
    )
    const werewolfCount = countRoleSeats(new Set(werewolfRoles))
    const villagerCount = selectedCustomVillagers.size
    const specialGoodCount = specialGoodRoles.length
    const totalTownCount = specialGoodCount + villagerCount

    return (
      villagerCount === 0
      || werewolfCount === 0
      || specialGoodCount === 0
      || werewolfCount >= totalTownCount
      || totalTownCount + werewolfCount > MAX_PLAYERS
      || (gameSettingForGame === 'USER_CUSTOM_CONFIG'
        && !isMultiplayer
        && !role)
    )
  }, [
    customExtraRole,
    customWolfChoice,
    gameSettingForGame,
    isMultiplayer,
    role,
    selectedCustomRoles,
    selectedCustomVillagers,
  ])

  const totalPlayerCount = useMemo(() => {
    if ((readSettingMode(gameSettingForGame).symbolicPlayerCount !== null))
      return readSettingMode(gameSettingForGame).symbolicPlayerCount!
    if (
      gameSettingForGame === 'USER_CUSTOM_CONFIG'
      || gameSettingForGame === 'HUMAN_VS_AI'
      || gameSettingForGame === 'WATCH_MODE'
      || gameSettingForGame === 'MODEL_COMPARISON'
      || gameSettingForGame === 'BYOM'
    ) {
      return countRoleSeats(selectedCustomRoles) + selectedCustomVillagers.size
    }
    const config = getRoleConfiguration(gameSettingForGame)
    return (
      config.goodRoles.filter(r => r.enabled).length
      + config.evilRoles.filter(r => r.enabled).length
      + config.villagerCount
    )
  }, [gameSettingForGame, selectedCustomRoles, selectedCustomVillagers])

  const isSheriffSettingEligible = isSheriffEligibleGameSetting(
    gameSettingForGame,
    totalPlayerCount,
  )
  const isSheriffEnabledForStart = isSheriffSettingEligible && hasSheriff

  const currentMaxPlayers = isFreeBillingMode
    ? FREE_GAME_MAX_PLAYERS
    : MAX_PLAYERS
  const exceedsMaxPlayers = totalPlayerCount > currentMaxPlayers
  const isStartAccessResolving
    = !accountCalendar.ready || !authResolved || !restrictionsData || restrictionsAreFetching
      || (isFundingChoiceUnresolved && gamePassSummary.isPending)
  const isStartAccessLoading = isStartAccessResolving && !accountCalendar.isError
  const startAccessRestriction = useMemo<StartAccessRestriction | null>(() => {
    if (isStartAccessResolving)
      return null

    const isAnonymousSettingAllowed
      = !isVariableSeatBoard(gameSettingForGame)
        && (isMultiplayer
          ? readRoster({ kind: 'selection', setting: gameSettingForGame, playMode }).freeGameEligible
          : gameSettingForGame === ANONYMOUS_FREE_GAME_SETTING)
    if (isUnregistered && !isAnonymousSettingAllowed) {
      return {
        resolution: 'signin',
        reasonKey: 'game.error.login_required_for_mode',
      }
    }

    if (
      isFreeBillingMode
      && !readRoster({ kind: 'selection', setting: gameSettingForGame, playMode }).freeGameEligible
    ) {
      return canPay
        ? {
            resolution: 'switch-to-paid',
            reasonKey: 'game.error.free_game_mode_not_allowed',
          }
        : {
            resolution: 'topup',
            reasonKey: 'game.error.game_mode_restricted',
          }
    }

    if (isFreeBillingMode && isFreeModeUnavailable) {
      return {
        resolution:
          user && canPay ? 'switch-to-paid' : user ? 'topup' : 'signin',
        reasonKey: 'game.error.free_games_exhausted',
      }
    }

    if (!isFreeBillingMode && !usesGamePass && !canUseCredits && !!user) {
      return { resolution: 'topup', reasonKey: 'game.error.game_mode_restricted' }
    }

    if (!isFreeBillingMode && isPaidModeUnavailable) {
      return {
        resolution: user ? 'topup' : 'signin',
        reasonKey: user
          ? 'game.error.game_mode_restricted'
          : 'game.error.login_required_for_mode',
      }
    }

    if (
      isFreeBillingMode
      && totalPlayerCount > FREE_GAME_MAX_PLAYERS
      && totalPlayerCount <= MAX_PLAYERS
    ) {
      return {
        resolution: canPay ? 'switch-to-paid' : user ? 'topup' : 'signin',
        reasonKey: 'game.error.free_game_max_players_exceeded',
      }
    }

    return null
  }, [
    canPay,
    canUseCredits,
    usesGamePass,
    gameSettingForGame,
    isFreeBillingMode,
    isFreeModeUnavailable,
    isMultiplayer,
    isPaidModeUnavailable,
    isStartAccessResolving,
    isUnregistered,
    playMode,
    totalPlayerCount,
    user,
  ])
  const startAccessRestrictionReason = startAccessRestriction
    ? t(startAccessRestriction.reasonKey)
    : ''
  const displayedActionError = accountCalendar.isError
    ? t('checkIn.timeZoneError')
    : startAccessRestrictionReason || validationError
  const freeGameMaxToastState = useRef({
    isFreeBillingMode,
    totalPlayerCount,
  })

  useEffect(() => {
    const previous = freeGameMaxToastState.current
    freeGameMaxToastState.current = {
      isFreeBillingMode,
      totalPlayerCount,
    }

    if (
      isFreeBillingMode
      && totalPlayerCount > FREE_GAME_MAX_PLAYERS
      && (!previous.isFreeBillingMode
        || totalPlayerCount > previous.totalPlayerCount)
    ) {
      toast.error(t('game.freeGameMaxPlayersToast'), {
        id: 'free-game-max-players',
      })
    }
  }, [isFreeBillingMode, t, totalPlayerCount])

  const specialRolesCount = useMemo(() => {
    const config = getRoleConfiguration(gameSettingForGame)
    if (
      gameSettingForGame === 'USER_CUSTOM_CONFIG'
      || gameSettingForGame === 'HUMAN_VS_AI'
      || gameSettingForGame === 'WATCH_MODE'
      || gameSettingForGame === 'MODEL_COMPARISON'
      || gameSettingForGame === 'BYOM'
    ) {
      return Array.from(selectedCustomRoles).filter(roleKey =>
        config.goodRoles.some(goodRole => goodRole.key === roleKey),
      ).length
    }
    return config.goodRoles.filter(r => r.enabled).length
  }, [gameSettingForGame, selectedCustomRoles])

  const evilRolesCount = useMemo(() => {
    const config = getRoleConfiguration(gameSettingForGame)
    if (
      gameSettingForGame === 'USER_CUSTOM_CONFIG'
      || gameSettingForGame === 'HUMAN_VS_AI'
      || gameSettingForGame === 'WATCH_MODE'
      || gameSettingForGame === 'MODEL_COMPARISON'
      || gameSettingForGame === 'BYOM'
    ) {
      return countRoleSeats(new Set(Array.from(selectedCustomRoles).filter(roleKey =>
        config.evilRoles.some(evilRole => evilRole.key === roleKey),
      )))
    }
    return config.evilRoles.filter(r => r.enabled).length
  }, [gameSettingForGame, selectedCustomRoles])

  const villagerCheckboxes = useMemo(() => {
    const villagerLimit = isFixedGameSetting(gameSettingForGame)
      ? getRoleConfiguration(gameSettingForGame).villagerCount
      : (readSettingMode(gameSettingForGame).symbolicPlayerCount !== null) ? 4 : 8
    return Array.from({ length: villagerLimit }, (_, i) => i).map((index) => {
      const isFixedG10Villager
        = (readSettingMode(gameSettingForGame).symbolicPlayerCount !== null) && index < 3
      const isCustomRoleConfig
        = gameSettingForGame === 'USER_CUSTOM_CONFIG'
          || gameSettingForGame === 'HUMAN_VS_AI'
          || gameSettingForGame === 'WATCH_MODE'
          || gameSettingForGame === 'MODEL_COMPARISON'
          || gameSettingForGame === 'BYOM'
      let enabled
        = index < getRoleConfiguration(gameSettingForGame).villagerCount
      if (isFixedG10Villager) {
        enabled = true
      }
      else if ((readSettingMode(gameSettingForGame).symbolicPlayerCount !== null)) {
        enabled = selectedD10Villagers.has(index)
      }
      else if (isCustomRoleConfig) {
        enabled = selectedCustomVillagers.has(index)
      }
      const isQuestionMark
        = (readSettingMode(gameSettingForGame).symbolicPlayerCount !== null) && index >= 3
      return { index, enabled, isFixedG10Villager, isQuestionMark }
    })
  }, [gameSettingForGame, selectedD10Villagers, selectedCustomVillagers])

  const passStartRequest = useRef<{ terms: string, requestId: string } | null>(null)
  const clearSubmittedPassStart = useCallback((requestId: string | undefined) => {
    if (requestId && passStartRequest.current?.requestId === requestId)
      passStartRequest.current = null
  }, [])

  const executeStartGame = useCallback(async () => {
    if (gamePassEligible && !isFreeBillingMode && selectedFundingSource === null)
      return
    if ([...selectedCustomRoles].some(roleKey =>
      isRoleDisabledForHumanVsAi(gameSettingForGame, roleKey))) {
      setValidationError(t('game.invalidRoleConfiguration'))
      return
    }
    let finalGameSetting: string

    if ((readSettingMode(gameSettingForGame).symbolicPlayerCount !== null)) {
      finalGameSetting = gameSettingForGame
    }
    else if (
      gameSettingForGame === 'USER_CUSTOM_CONFIG'
      || gameSettingForGame === 'HUMAN_VS_AI'
      || gameSettingForGame === 'WATCH_MODE'
      || gameSettingForGame === 'MODEL_COMPARISON'
      || gameSettingForGame === 'BYOM'
    ) {
      try {
        const villagerCount = selectedCustomVillagers.size
        const selectedRoles = roleCountsForChoices(selectedCustomRoles)

        const choices = customRosterSelections(selectedCustomRoles)
        let generatedSetting = usesCustomRosterTemplate(gameSettingForGame)
          ? buildCustomRosterSetting(customPlayerCount, choices.extraRole ?? '', choices.wolfChoice ?? '')
          : generateRoleCodeStringFromCheckbox('game', selectedRoles, villagerCount)
        if (gameSettingForGame === 'WATCH_MODE') {
          generatedSetting = writeSetting({ kind: 'select-mode', setting: generatedSetting, mode: 'W' })
        }
        if (gameSettingForGame === 'MODEL_COMPARISON') {
          generatedSetting = writeSetting({ kind: 'select-mode', setting: generatedSetting, mode: 'C' })
        }
        if (gameSettingForGame === 'BYOM') {
          generatedSetting = writeSetting({ kind: 'select-mode', setting: generatedSetting, mode: 'B' })
        }
        finalGameSetting = generatedSetting
      }
      catch (error) {
        console.error('Error generating GameSetting:', error)
        setValidationError(t('game.invalidRoleConfiguration'))
        return
      }
    }
    else {
      finalGameSetting = gameSettingForGame
    }

    if (
      gameSettingForGame === 'BYOM'
      && hasCustomProvider
      && customEndpointData
    ) {
      const hasCustomRole
        = Object.values(normalizedByomRoleAssignments).includes('custom')
      if (hasCustomRole) {
        try {
          const validation = await validateByomEndpoint({
            endpoint: customEndpointData.customEndpoint,
            apiKey: customEndpointData.customApiKey,
            modelId: customEndpointData.customModelId,
          })
          if (!validation.success) {
            setValidationError(
              t('game.error.byom_endpoint_validation_failed'),
            )
            return
          }
        }
        catch {
          setValidationError(t('game.error.byom_endpoint_validation_failed'))
          return
        }
      }
    }

    let submittedPassRequestId: string | undefined
    try {
      const startTimeZone = await accountCalendar.initialize()
      const requestQuotaWindowStartMs = getFreeGameQuotaWindowStart(
        new Date(currentQuotaTime()),
        startTimeZone,
      ).getTime()
      const startInput = {
        language: language as LanguageType,
        gameSetting: finalGameSetting,
        ...(gameSettingForGame === 'WATCH_MODE'
          || gameSettingForGame === 'MODEL_COMPARISON'
          ? {}
          : { role: role as PlayerRoleType }),
        isHighPerformance: effectiveHighPerformance,
        freeGame: effectiveFreeGame,
        ...(!isFreeBillingMode && gamePassEligible && selectedFundingSource !== null
          ? { fundingSource: selectedFundingSource }
          : {}),
        hasSheriff: isSheriffEnabledForStart,
        ...(gameSettingForGame === 'HUMAN_VS_AI'
          ? { controlAllEvilSeats: true }
          : {}),
        ...(!isFreeBillingMode
          && supportsPlatformRoleModelSelection
          && Object.keys(normalizedRoleModelAssignments).length > 0
          ? {
              modelAssignments: normalizedRoleModelAssignments,
              preserveHumanSeats: true,
            }
          : {}),
        ...(gameSettingForGame === 'MODEL_COMPARISON'
          && comparedRuntimeRoleKey
          && hasValidComparisonModels
          ? {
              comparedRoleKey: comparedRuntimeRoleKey,
              modelA: comparisonModels[0],
              modelB: comparisonModels[1],
              preserveHumanSeats: true,
            }
          : {}),
        ...(gameSettingForGame === 'BYOM'
          ? (() => {
              const { byomAssignments, officialModelAssignments }
                = splitByomSeatAssignments(normalizedByomRoleAssignments)

              const result: Record<string, unknown> = {}

              if (Object.keys(byomAssignments).length > 0) {
                const conf: ByomProviderConfig | null
                  = customEndpointData?.customApiKey
                    && customEndpointData?.customEndpoint
                    ? {
                        apiKey: customEndpointData.customApiKey,
                        endpoint: customEndpointData.customEndpoint,
                        modelId: customEndpointData.customModelId,
                      }
                    : null
                if (conf) {
                  result.byomConfigs = { custom: conf }
                  result.byomRoleAssignments = byomAssignments
                }
              }

              if (Object.keys(officialModelAssignments).length > 0) {
                result.modelAssignments = officialModelAssignments
                result.preserveHumanSeats = true
              }

              return result
            })()
          : {}),
      }
      const terms = JSON.stringify(startInput, (_key, value: unknown) => (
        value && typeof value === 'object' && !Array.isArray(value)
          ? Object.fromEntries(Object.entries(value).sort(([a], [b]) => a.localeCompare(b)))
          : value
      ))
      if (usesGamePass && passStartRequest.current?.terms !== terms)
        passStartRequest.current = { terms, requestId: crypto.randomUUID() }
      if (!usesGamePass)
        passStartRequest.current = null
      submittedPassRequestId = usesGamePass ? passStartRequest.current?.requestId : undefined
      const res = await startGameAsync({
        ...startInput,
        ...(submittedPassRequestId
          ? { requestId: submittedPassRequestId }
          : {}),
      })

      if (res?.gameId) {
        clearSubmittedPassStart(submittedPassRequestId)
        if (isAnonymous) {
          setAnonymousBrowserFreeGamesUsed(
            recordAnonymousFreeGameStartedInWindow(
              res.gameId,
              requestQuotaWindowStartMs,
              undefined,
              currentQuotaTime(),
              startTimeZone,
            ),
          )
        }
        const route = getStartedGameRoute(res.gameId, gameSettingForGame)
        queryClient.invalidateQueries({
          queryKey: trpc.userRouter.getGameRestrictions.queryOptions().queryKey,
        })
        void queryClient.invalidateQueries({ queryKey: trpc.promoGamePassRouter.summary.queryKey() })
        navigate(route)
      }
    }
    catch (error) {
      queryClient.invalidateQueries({
        queryKey: trpc.userRouter.getGameRestrictions.queryOptions().queryKey,
      })

      if (!(error instanceof TRPCClientError)) {
        setValidationError(t('game.error.start_game_failed'))
        return
      }

      const errorKey = error.message || 'start_game_failed'
      const passError = errorKey === 'game_pass_unavailable' || errorKey === 'game_pass_ineligible' || errorKey === 'game_pass_start_terms_changed'
      if (passError) {
        if (errorKey === 'game_pass_start_terms_changed')
          clearSubmittedPassStart(submittedPassRequestId)
        setValidationError(t(`gamePasses.errors.${errorKey}`))
        void gamePassSummary.refetch()
        return
      }
      const translated = t(`game.error.${errorKey}`)
      const displayMessage
        = translated !== `game.error.${errorKey}`
          ? translated
          : t('game.error.start_game_failed')

      switch (error.data?.code) {
        case 'TOO_MANY_REQUESTS': {
          setValidationError(displayMessage)

          break
        }
        case 'PRECONDITION_FAILED': {
          setValidationError(displayMessage)

          break
        }
        case 'FORBIDDEN': {
          switch (errorKey) {
            case 'game_mode_restricted': {
              setValidationError(displayMessage)

              break
            }
            case 'credits_exhausted': {
              setValidationError(displayMessage)

              break
            }
            default: {
              toast.error(displayMessage)
            }
          }

          break
        }
        case 'UNAUTHORIZED': {
          setValidationError(displayMessage)

          break
        }
        default: {
          setValidationError(displayMessage)
        }
      }
    }
  }, [
    gameSettingForGame,
    accountCalendar.initialize,
    currentQuotaTime,
    t,
    selectedCustomVillagers,
    selectedCustomRoles,
    customPlayerCount,
    startGameAsync,
    clearSubmittedPassStart,
    language,
    role,
    effectiveHighPerformance,
    effectiveFreeGame,
    gamePassEligible,
    gamePassSummary.refetch,
    selectedFundingSource,
    usesGamePass,
    isSheriffEnabledForStart,
    isAnonymous,
    navigate,
    normalizedRoleModelAssignments,
    comparisonModels,
    comparedRuntimeRoleKey,
    hasValidComparisonModels,
    byomRoleAssignments,
    normalizedByomRoleAssignments,
    customEndpointData,
    hasCustomProvider,
    validateByomEndpoint,
    queryClient,
    trpc.userRouter.getGameRestrictions,
    trpc.promoGamePassRouter.summary,
    supportsPlatformRoleModelSelection,
    isFreeBillingMode,
  ])

  const handleStartGame = useCallback(async () => {
    if (redirectToEmailVerificationIfRequired())
      return
    if (
      isFreeBillingMode
      && !readRoster({ kind: 'selection', setting: gameSettingForGame, playMode: 'solo' }).freeGameEligible
    ) {
      setValidationError(t('game.error.free_game_mode_not_allowed'))
      return
    }

    const canStartAnonymously
      = !isMultiplayer
        && gameSettingForGame === ANONYMOUS_FREE_GAME_SETTING
        && billingMode === 'free'

    if (!user && !actor) {
      localStorage.setItem('mentiss_pending_game_start', 'true')
      if (canStartAnonymously) {
        const signedIn = await signInAnonymously()
        if (!signedIn) {
          localStorage.removeItem('mentiss_pending_game_start')
          setValidationError(t('game.error.start_game_failed'))
          return
        }
        await queryClient.invalidateQueries({
          queryKey: trpc.userRouter.getGameRestrictions.queryOptions().queryKey,
        })
        return
      }

      navigate(
        signInUrlReturningTo(returnTargetFrom(window.location)),
      )
      return
    }

    if (isAnonymous && !canStartAnonymously) {
      localStorage.setItem('mentiss_pending_game_start', 'true')
      navigate(
        signInUrlReturningTo(returnTargetFrom(window.location)),
      )
      return
    }

    if (
      gameSettingForGame === 'BYOM'
      && availableModels.length === 0
      && !hasCustomProvider
    ) {
      setValidationError(t('game.error.byom_not_configured'))
      return
    }

    if (gameSettingForGame === 'BYOM') {
      const hasCustomRole
        = Object.values(normalizedByomRoleAssignments).includes('custom')
      if (!hasCustomRole) {
        setValidationError(t('game.error.byom_requires_custom_model'))
        return
      }
    }

    if (
      !(readSettingMode(gameSettingForGame).symbolicPlayerCount !== null)
      && gameSettingForGame !== 'HUMAN_VS_AI'
      && gameSettingForGame !== 'WATCH_MODE'
      && gameSettingForGame !== 'MODEL_COMPARISON'
      && gameSettingForGame !== 'BYOM'
      && !role
    ) {
      return
    }

    if (gameSettingForGame === 'MODEL_COMPARISON'
      && (!comparedRuntimeRoleKey || !hasValidComparisonModels)) {
      return
    }

    if (
      gameSettingForGame === 'USER_CUSTOM_CONFIG'
      || gameSettingForGame === 'HUMAN_VS_AI'
      || gameSettingForGame === 'WATCH_MODE'
      || gameSettingForGame === 'MODEL_COMPARISON'
      || gameSettingForGame === 'BYOM'
    ) {
      const config = getRoleConfiguration(gameSettingForGame)
      const selectedRoles = Array.from(selectedCustomRoles)
      const villagerCount = selectedCustomVillagers.size

      const evilCount = countRoleSeats(new Set(selectedRoles.filter(role =>
        config.evilRoles.some(evilRole => evilRole.key === role),
      )))

      const goodCount = selectedRoles.filter(role =>
        config.goodRoles.some(goodRole => goodRole.key === role),
      ).length

      if (
        goodCount === 0
        || evilCount === 0
        || goodCount + villagerCount <= evilCount
      ) {
        setValidationError(t('game.roleConfigNotBalanced'))
        return
      }

      setValidationError('')
    }

    executeStartGame()
  }, [
    redirectToEmailVerificationIfRequired,
    user,
    actor,
    isAnonymous,
    isMultiplayer,
    signInAnonymously,
    navigate,
    gameSettingForGame,
    role,
    comparedRuntimeRoleKey,
    hasValidComparisonModels,
    selectedCustomRoles,
    selectedCustomVillagers,
    t,
    setValidationError,
    executeStartGame,
    language,
    availableModels,
    hasCustomProvider,
    normalizedByomRoleAssignments,
    billingMode,
    isFreeBillingMode,
    queryClient,
    trpc.userRouter.getGameRestrictions,
  ])

  const handlePlayModeChange = (mode: PlayMode) => {
    if (mode === 'multiplayer' && !authResolved)
      return

    setPlayMode(mode)
    try {
      localStorage.setItem(LAST_PLAY_MODE_STORAGE_KEY, mode)
    }
    catch {}
    setValidationError('')
    if (
      (mode === 'multiplayer' && !MULTIPLAYER_SETTINGS.has(gameSettingForGame))
      || (isFreeBillingMode
        && !readRoster({ kind: 'selection', setting: tenSeatSettingFor(gameSettingForGame), playMode: mode }).freeGameEligible)
    ) {
      handleGameSettingChange(DEFAULT_GAME_SETTING, billingMode, mode)
    }
  }

  useEffect(() => {
    if (isMultiplayer && !MULTIPLAYER_SETTINGS.has(gameSettingForGame)) {
      handleGameSettingChange(GameSettingOptions.G6_1SR1WT_2WW_2VG)
    }
  }, [gameSettingForGame, isMultiplayer])

  useEffect(() => {
    if (!authResolved || user)
      return

    if (billingMode !== 'free')
      setBillingMode('free')
  }, [authResolved, user])

  const handleSheriffToggleChange = (checked: boolean | 'indeterminate') => {
    const nextHasSheriff = checked === true
    setHasSheriff(nextHasSheriff)
    try {
      localStorage.setItem(HAS_SHERIFF_STORAGE_KEY, String(nextHasSheriff))
    }
    catch {}
  }

  const handleResumeGames = useCallback(() => {
    const resumeGamesPath = `${RESUME_GAMES_PATH}&mode=${isMultiplayer ? 'multiplayer' : 'solo'}`

    if (!user) {
      navigate(`/signin?redirect=${encodeURIComponent(resumeGamesPath)}`)
      return
    }
    navigate(resumeGamesPath)
  }, [isMultiplayer, navigate, user])

  const handleCreateRoom = useCallback(async (listed: boolean) => {
    if (gamePassEligible && !isFreeBillingMode && selectedFundingSource === null)
      return
    if (redirectToEmailVerificationIfRequired())
      return
    const normalizedRoomName = roomName.trim()
    if (!normalizedRoomName) {
      setValidationError(t('game.error.room_name_required'))
      roomNameInputRef.current?.focus()
      return
    }
    if ([...selectedCustomRoles].some(roleKey =>
      isRoleDisabledForHumanVsAi(gameSettingForGame, roleKey))) {
      setValidationError(t('game.invalidRoleConfiguration'))
      return
    }
    if (
      isFreeBillingMode
      && !readRoster({ kind: 'selection', setting: gameSettingForGame, playMode: 'multiplayer' }).freeGameEligible
    ) {
      setValidationError(t('game.error.free_game_mode_not_allowed'))
      return
    }

    if (!user) {
      navigate(
        signInUrlReturningTo(returnTargetFrom(window.location)),
      )
      return
    }

    let gameSetting: string
    if ((readSettingMode(gameSettingForGame).symbolicPlayerCount !== null)) {
      gameSetting = gameSettingForGame
    }
    else if (
      gameSettingForGame === 'USER_CUSTOM_CONFIG'
      || gameSettingForGame === 'HUMAN_VS_AI'
    ) {
      const config = getRoleConfiguration(gameSettingForGame)
      const selectedRoles = Array.from(selectedCustomRoles)
      const villagerCount = selectedCustomVillagers.size
      const evilCount = countRoleSeats(new Set(selectedRoles.filter(r =>
        config.evilRoles.some(er => er.key === r),
      )))
      const goodCount = selectedRoles.filter(r =>
        config.goodRoles.some(gr => gr.key === r),
      ).length
      if (
        goodCount === 0
        || evilCount === 0
        || goodCount + villagerCount <= evilCount
      ) {
        setValidationError(t('game.roleConfigNotBalanced'))
        return
      }
      const roleCounts = roleCountsForChoices(selectedCustomRoles)
      if (usesCustomRosterTemplate(gameSettingForGame)) {
        const choices = customRosterSelections(selectedCustomRoles)
        try {
          gameSetting = buildCustomRosterSetting(customPlayerCount, choices.extraRole ?? '', choices.wolfChoice ?? '')
        }
        catch {
          setValidationError(t('game.invalidRoleConfiguration'))
          return
        }
      }
      else {
        gameSetting = generateRoleCodeStringFromCheckbox('game', roleCounts, villagerCount)
      }
    }
    else {
      gameSetting = gameSettingForGame
    }

    setValidationError('')
    try {
      const res = await createRoomAsync({
        gameSetting,
        language: language as LanguageType,
        listed,
        name: normalizedRoomName,
        paymentMode: effectiveFreeGame ? 'split' : roomPaymentMode,
        ...(!isFreeBillingMode && gamePassEligible && selectedFundingSource !== null ? { fundingSource: selectedFundingSource } : {}),
        humanVsAi: gameSettingForGame === 'HUMAN_VS_AI',
        freeGame: effectiveFreeGame,
        isHighPerformance: effectiveHighPerformance,
        hasSheriff: isSheriffEnabledForStart,
        ...(!isFreeBillingMode
          && supportsPlatformRoleModelSelection
          && Object.keys(normalizedRoleModelAssignments).length > 0
          ? { modelAssignments: normalizedRoleModelAssignments }
          : {}),
      })
      navigate(`/room/${res.roomCode ?? res.inviteCode}`)
    }
    catch (error) {
      if (!(error instanceof TRPCClientError)) {
        toast.error(t('game.error.start_game_failed'))
        return
      }
      const key = error.message || 'start_game_failed'
      if (key === 'game_pass_unavailable' || key === 'game_pass_ineligible') {
        setValidationError(t(`gamePasses.errors.${key}`))
        void gamePassSummary.refetch()
        return
      }
      const translated = t(`game.error.${key}`)
      const displayMessage = translated !== `game.error.${key}`
        ? translated
        : t('game.error.start_game_failed')
      setValidationError(displayMessage)
      toast.error(displayMessage)
    }
  }, [
    redirectToEmailVerificationIfRequired,
    user,
    navigate,
    t,
    roomName,
    roomPaymentMode,
    gameSettingForGame,
    selectedCustomRoles,
    selectedCustomVillagers,
    customPlayerCount,
    createRoomAsync,
    gamePassEligible,
    gamePassSummary,
    selectedFundingSource,
    effectiveFreeGame,
    effectiveHighPerformance,
    isSheriffEnabledForStart,
    language,
    normalizedRoleModelAssignments,
    supportsPlatformRoleModelSelection,
    isFreeBillingMode,
  ])

  useEffect(() => {
    if (
      !actor
      || isStartAccessResolving
      || isFundingChoiceUnresolved
      || localStorage.getItem('mentiss_pending_game_start') !== 'true'
    ) {
      return
    }

    localStorage.removeItem('mentiss_pending_game_start')
    if (startAccessRestriction || isStarting)
      return

    void handleStartGame()
  }, [
    actor,
    isStartAccessResolving,
    isFundingChoiceUnresolved,
    isStarting,
    handleStartGame,
    startAccessRestriction,
  ])

  useEffect(() => {
    if (searchParams.get('mp') === null || !authResolved)
      return
    handlePlayModeChange('multiplayer')
    const next = new URLSearchParams(window.location.search)
    next.delete('mp')
    setSearchParams(next, { replace: true })
  }, [authResolved, searchParams])

  const launchLatch = useSingleFlight()

  const primaryActionIsPending = isMultiplayer
    ? isCreatingRoom
    : isStarting
  const isFreeGameSettingUnavailable
    = isFreeBillingMode && !readRoster({ kind: 'selection', setting: gameSettingForGame, playMode }).freeGameEligible
  const primaryActionHasConfigurationError = isMultiplayer
    ? isCustomConfigDisabled || exceedsMaxPlayers || !roomName.trim()
    : (!(readSettingMode(gameSettingForGame).symbolicPlayerCount !== null)
      && gameSettingForGame !== 'HUMAN_VS_AI'
      && gameSettingForGame !== 'WATCH_MODE'
      && gameSettingForGame !== 'MODEL_COMPARISON'
      && gameSettingForGame !== 'BYOM'
      && !role)
    || (gameSettingForGame === 'MODEL_COMPARISON'
      && (!comparedRuntimeRoleKey || !hasValidComparisonModels))
    || (gameSettingForGame === 'BYOM'
      && availableModels.length === 0
      && !hasCustomProvider)
    || isCustomConfigDisabled
    || exceedsMaxPlayers
  const primaryActionIsDisabled
    = primaryActionIsPending
      || isStartAccessResolving
      || isFundingChoiceUnresolved
      || isFreeGameSettingUnavailable
      || (!startAccessRestriction && primaryActionHasConfigurationError)

  const handlePrimaryAction = (listed = false) => {
    if (redirectToEmailVerificationIfRequired())
      return
    if (isStartAccessResolving || isFundingChoiceUnresolved || primaryActionIsPending || isFreeGameSettingUnavailable)
      return

    if (startAccessRestriction) {
      if (startAccessRestriction.resolution === 'signin') {
        navigate(signInUrlReturningTo(returnTargetFrom(window.location)))
        return
      }

      if (startAccessRestriction.resolution === 'topup') {
        navigate('/dashboard?tab=subscription')
        return
      }

      setBillingMode('quick')
      setValidationError('')
      return
    }

    void runSingleFlight(launchLatch, () =>
      isMultiplayer ? handleCreateRoom(listed) : handleStartGame())
  }

  const renderPlatformModelSelector = (
    sourceSeatKey: string,
    runtimeSeatKey: string,
    isPossibleSeat: boolean,
  ) => {
    if (
      !isModelConfigurationVisible
      || !supportsPlatformRoleModelSelection
      || !isPossibleSeat
      || availableModels.length === 0
    ) {
      return null
    }

    if (
      isExplicitHumanModelSeat({
        seatKey: runtimeSeatKey,
        gameSetting: gameSettingForGame,
        selectedRole: role,
        isMultiplayer,
        evilSeatKeys: runtimeEvilSeatKeys,
      })
    ) {
      return (
        <span
          data-testid={`role-model-human-${runtimeSeatKey}`}
          className="rounded-md border border-emerald-400/40 bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-700 dark:text-emerald-300"
        >
          {t('game.room.you')}
        </span>
      )
    }

    return (
      <select
        data-testid={`role-model-select-${runtimeSeatKey}`}
        data-seat-key={runtimeSeatKey}
        data-source-seat-key={sourceSeatKey}
        value={roleModelAssignments[sourceSeatKey] || ''}
        onChange={event =>
          handleRoleModelChange(sourceSeatKey, event.target.value)}
        className="h-6 max-w-[160px] truncate rounded-md border border-cyan-400/40 bg-gradient-to-r from-cyan-500/10 to-cyan-500/5 px-1.5 text-[11px] text-foreground transition-colors hover:border-cyan-400/60 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500/40 dark:from-cyan-500/15 dark:to-cyan-500/5"
      >
        <option value="" disabled>
          {t('game.selectModel')}
        </option>
        {availableModels.map(model => (
          <option key={model} value={model}>
            {getModelDisplayName(model)}
          </option>
        ))}
      </select>
    )
  }
  const renderCustomSeatControls = (key: string, enabled: boolean) => (
    <>
      {renderPlatformModelSelector(key, runtimeSeatKeyBySource.get(key) ?? key, enabled)}
      {gameSettingForGame === 'BYOM' && isModelConfigurationVisible && enabled
        && (availableModels.length > 0 || hasCustomProvider) && (
        <select
          data-testid={`byom-model-select-${key}`}
          aria-label={`${getModelSeatName(t, key)} ${t('game.selectModel')}`}
          value={byomRoleAssignments[key] || ''}
          onChange={event => setByomRoleAssignments(current => ({ ...current, [key]: event.target.value }))}
          className="h-6 max-w-[160px] truncate rounded-md border border-amber-400/40 bg-gradient-to-r from-amber-500/10 to-amber-500/5 px-1.5 text-[11px] text-foreground transition-colors hover:border-amber-400/60 focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500/40 dark:from-amber-500/15 dark:to-amber-500/5"
        >
          {availableModels.map(model => <option key={model} value={model}>{getModelDisplayName(model)}</option>)}
          {hasCustomProvider && <option value="custom">(You) {customModelLabel}</option>}
        </select>
      )}
    </>
  )
  const canConfigureModels = !isFreeBillingMode
    && ((supportsPlatformRoleModelSelection && availableModels.length > 0)
      || (gameSettingForGame === 'BYOM'
        && (availableModels.length > 0 || hasCustomProvider)))

  if (searchParams.get('mode') === 'tournament') {
    return (
      <TournamentLauncher
        editionId={searchParams.get('edition') || undefined}
        settings={visibleGameSettings}
        onMode={(mode) => {
          const next = new URLSearchParams(searchParams)
          next.delete('mode')
          next.delete('edition')
          setSearchParams(next)
          setBillingMode(mode)
        }}
      />
    )
  }

  return (
    <div
      data-testid="game-launcher"
      className="min-h-dvh bg-section-dark px-4 py-20 sm:px-6 lg:px-8"
    >
      <div className="mx-auto max-w-7xl">
        <div className="grid gap-8 lg:grid-cols-3">
          { }
          <Card className="flex flex-col lg:col-span-1 lg:min-h-[600px]">
            <CardHeader>
              <CardTitle>
                <Heading className="flex items-center gap-2">
                  <Play className="size-5" />
                  {t('game.gameConfiguration')}
                  {isAdmin && (
                    <Badge variant="destructive" className="ml-2">
                      <Crown className="mr-1 size-3" />
                      Admin Mode
                    </Badge>
                  )}
                </Heading>
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-1 flex-col">
              {
              }
              <div
                className="grid grid-cols-2 gap-2"
                data-testid="play-mode-toggle"
              >
                <Button
                  type="button"
                  variant={isMultiplayer ? 'outline' : 'default'}
                  onClick={() => handlePlayModeChange('solo')}
                  data-testid="mode-solo"
                  className="w-full"
                >
                  {t('game.soloGame')}
                </Button>
                <Button
                  type="button"
                  variant={isMultiplayer ? 'default' : 'outline'}
                  onClick={() => handlePlayModeChange('multiplayer')}
                  disabled={!authResolved}
                  data-testid="mode-multiplayer"
                  className="w-full"
                >
                  {t('game.multiplayer')}
                </Button>
              </div>
              {isUnregistered && (
                <p
                  className="mt-2 text-xs text-muted-foreground"
                  data-testid="multiplayer-sign-in-required"
                >
                  {t('game.multiplayerSignInRequired')}
                </p>
              )}

              { }
              <div className="mt-4 space-y-2">
                <div className="flex flex-wrap gap-1.5">
                  {availableGameSettings.map(setting => renderGameSettingTab(setting))}
                </div>
                {lockedGameSettings.length > 0 && (
                  <div className="space-y-3 pt-2">
                    {availableGameSettings.length > 0 && (
                      <div
                        data-testid="board-lock-divider"
                        className="mx-auto h-px w-10 bg-primary/50"
                        aria-hidden="true"
                      />
                    )}
                    <div className="flex flex-wrap gap-1.5">
                      {lockedGameSettings.map(setting => renderGameSettingTab(setting))}
                    </div>
                  </div>
                )}
              </div>

              { }
              {!isMultiplayer && (
                <div className="mt-4 space-y-2">
                  <Label htmlFor="role" className="flex items-center gap-2">
                    <Crown className="size-4" />
                    {gameSettingForGame === 'MODEL_COMPARISON'
                      ? t('game.comparedRole')
                      : t('game.yourRole')}
                  </Label>
                  <Select
                    value={
                      gameSettingForGame === 'MODEL_COMPARISON'
                        ? comparedRoleKey
                        : role
                    }
                    onValueChange={
                      gameSettingForGame === 'MODEL_COMPARISON'
                        ? setComparedRoleKey
                        : handleRoleChange
                    }
                    disabled={(readSettingMode(gameSettingForGame).symbolicPlayerCount !== null)
                      || gameSettingForGame === 'HUMAN_VS_AI'
                      || gameSettingForGame === 'WATCH_MODE'
                      || gameSettingForGame === 'BYOM'}
                  >
                    <SelectTrigger id="role">
                      {gameSettingForGame === 'HUMAN_VS_AI' ? (
                        <span className="text-muted-foreground">
                          {t('game.roleAllWerewolves')}
                        </span>
                      ) : gameSettingForGame === 'WATCH_MODE'
                        || gameSettingForGame === 'BYOM' ? (
                            <span className="text-muted-foreground">
                              {t('game.roleNoHumanPlayer')}
                            </span>
                          ) : gameSettingForGame === 'MODEL_COMPARISON' ? (
                            <SelectValue
                              placeholder={t('game.selectComparedRole')}
                            />
                          ) : (
                            <SelectValue
                              placeholder={
                                (readSettingMode(gameSettingForGame).symbolicPlayerCount !== null)
                                  ? t('game.roleRandomForG10')
                                  : t('game.selectYourRole')
                              }
                            />
                          )}
                    </SelectTrigger>
                    <SelectContent>
                      {gameSettingForGame === 'MODEL_COMPARISON'
                        ? comparisonRoleOptions.map(seat => (
                            <SelectItem
                              key={seat.sourceKey}
                              value={seat.sourceKey}
                              data-runtime-seat-key={seat.runtimeKey}
                            >
                              {getModelSeatName(t, seat.runtimeKey)}
                            </SelectItem>
                          ))
                        : roleOptions.map(option => (
                            <SelectItem key={option} value={option}>
                              {getRoleName(t, option)}
                            </SelectItem>
                          ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {(isFreeBillingMode || isSheriffSettingEligible) && (
                <div
                  className="mt-4 flex items-start gap-3"
                  data-testid="sheriff-toggle-container"
                >
                  <Checkbox
                    id="has-sheriff"
                    checked={isSheriffSettingEligible && hasSheriff}
                    disabled={!isSheriffSettingEligible}
                    onCheckedChange={handleSheriffToggleChange}
                    data-testid="sheriff-toggle"
                  />
                  <div className="grid gap-1 leading-none">
                    <Label
                      htmlFor="has-sheriff"
                      className="inline-flex cursor-pointer items-center gap-1.5 text-sm font-medium text-foreground"
                    >
                      <span aria-hidden="true">
                        🎖️
                      </span>
                      {t('game.sheriffToggle')}
                    </Label>
                  </div>
                </div>
              )}

              {(isFreeBillingMode || canConfigureModels) && (
                <div
                  className="mt-4 flex items-start gap-3"
                  data-testid="model-configuration-toggle-container"
                >
                  <Checkbox
                    id="model-configuration"
                    checked={canConfigureModels && showModelConfiguration}
                    disabled={!canConfigureModels}
                    onCheckedChange={(checked) => {
                      if (canConfigureModels)
                        setShowModelConfiguration(checked === true)
                    }}
                    data-testid="model-configuration-toggle"
                  />
                  <Label
                    htmlFor="model-configuration"
                    className={`inline-flex items-center gap-1.5 text-sm font-medium ${canConfigureModels ? 'cursor-pointer text-foreground' : 'cursor-not-allowed text-muted-foreground'}`}
                  >
                    <span aria-hidden="true">⚙️</span>
                    {t('game.modelConfiguration')}
                  </Label>
                </div>
              )}

              <div className="mt-3">
                <JevRoutingStatus />
              </div>

              {gameSettingForGame === 'MODEL_COMPARISON' && (
                <div className="mt-4 space-y-3">
                  {comparisonModels.map((modelId, idx) => (
                    <div key={idx} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label
                          htmlFor={`comparison-model-${idx}`}
                          className="flex items-center gap-2"
                        >
                          <span className="flex size-5 items-center justify-center rounded-sm bg-amber-500/15 text-xs font-bold text-amber-600 dark:text-amber-400">
                            {String.fromCodePoint(65 + idx)}
                          </span>
                          {t('game.selectModel')} {idx + 1}
                        </Label>
                      </div>
                      <Select
                        value={modelId}
                        onValueChange={(val) => {
                          if (isJevComparisonModel(val)
                            && isJevComparisonModel(comparisonModels[1 - idx] ?? '')) {
                            return
                          }
                          const newModels = [...comparisonModels]
                          newModels[idx] = val
                          setComparisonModels(newModels)
                        }}
                      >
                        <SelectTrigger
                          id={`comparison-model-${idx}`}
                          data-testid={idx === 0 ? 'comparison-model-a' : 'comparison-model-b'}
                        >
                          <SelectValue placeholder={t('game.selectModel')} />
                        </SelectTrigger>
                        <SelectContent
                          className="pointer-events-auto max-h-60 w-[var(--radix-select-trigger-width)] min-w-0 overflow-y-auto"
                          position="popper"
                          align="start"
                          side="bottom"
                        >
                          {comparisonModelOptions.map(model => (
                            <SelectItem
                              key={model}
                              value={model}
                              disabled={isJevComparisonModel(model)
                                && isJevComparisonModel(comparisonModels[1 - idx] ?? '')}
                            >
                              {isJevComparisonModel(model) ? 'Jev' : model}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-auto space-y-4 pt-4">
                <div className="border-t border-border" />

                {
                }
                <div className="flex flex-col gap-2 pt-4">
                  <div
                    role="radiogroup"
                    aria-label={t('game.billingMode')}
                    className="grid gap-3"
                  >
                    {billingModeOptions.map((option) => {
                      const isSelected = billingMode === option.value
                      const isDisabled
                        = option.value === 'free'
                          ? isFreeModeUnavailable
                          : isPaidModeUnavailable

                      return (
                        <button
                          id={`billing-mode-${option.value}`}
                          key={option.value}
                          type="button"
                          role="radio"
                          aria-checked={isSelected}
                          disabled={isDisabled}
                          data-testid={`billing-mode-${option.value}`}
                          onClick={() => {
                            setBillingMode(option.value)
                            if (option.value === 'free') {
                              const freeSetting = tenSeatSettingFor(gameSettingForGame)
                              handleGameSettingChange(
                                readRoster({ kind: 'selection', setting: freeSetting, playMode }).freeGameEligible
                                  ? freeSetting
                                  : DEFAULT_GAME_SETTING,
                                'free',
                              )
                            }
                          }}
                          className={`flex w-full items-start gap-3 text-left transition-colors ${
                            isDisabled
                              ? 'cursor-not-allowed text-muted-foreground/45'
                              : 'text-foreground'
                          }`}
                        >
                          <span
                            className={`mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border ${
                              isSelected
                                ? 'border-primary'
                                : 'border-muted-foreground/50'
                            }`}
                            aria-hidden="true"
                          >
                            {isSelected && (
                              <span className="size-2 rounded-full bg-primary" />
                            )}
                          </span>
                          <span className="min-w-0">
                            <span className="block text-sm font-medium leading-5">
                              {t(option.titleKey)}
                            </span>
                          </span>
                        </button>
                      )
                    })}
                  </div>

                  {
                  }
                  <TournamentEntry onSelect={(editionId) => {
                    const next = new URLSearchParams(searchParams)
                    next.set('mode', 'tournament')
                    next.set('edition', editionId)
                    setSearchParams(next)
                  }}
                  />
                  {!user && isMultiplayer && (
                    <p className="text-xs text-muted-foreground">
                      {t('game.multiplayerSignInRequired')}
                      <Link
                        to={signInUrlReturningTo(returnTargetFrom(window.location))}
                        className="ml-1 font-medium underline hover:text-foreground"
                      >
                        {t('nav.signIn')}
                      </Link>
                    </p>
                  )}
                  {!user && !isMultiplayer && (
                    <p className="text-xs text-muted-foreground">
                      {t('game.freeGameSignInPrompt')}
                      <Link
                        to={signInUrlReturningTo(returnTargetFrom(window.location))}
                        className="ml-1 font-medium underline hover:text-foreground"
                      >
                        {t('nav.signIn')}
                      </Link>
                    </p>
                  )}
                  {accountCalendar.ready && !!restrictionsData && (actor || isUnregistered) && hasFreeGames && (
                    <p className="text-xs text-muted-foreground" data-testid="free-game-available">
                      {freeGameAvailableCopy.replace('{date}', quotaResetLabel)}
                    </p>
                  )}
                  {accountCalendar.ready && (actor || isUnregistered) && !!restrictionsData && (
                    <p className="text-xs text-muted-foreground" data-testid="account-time-zone">
                      {t('checkIn.timeZone').replace('{timeZone}', accountTimeZone)}
                    </p>
                  )}
                  {accountCalendar.ready && (actor || isUnregistered) && !!restrictionsData && !hasFreeGames && (
                    <p className="text-xs text-muted-foreground">
                      {t('game.freeGameNoneLeft').replace(
                        '{date}',
                        quotaResetLabel,
                      )}
                      {!user ? (
                        <Link
                          to={signInUrlReturningTo(returnTargetFrom(window.location))}
                          className="ml-1 font-medium underline hover:text-foreground"
                        >
                          {t('nav.signIn')}
                        </Link>
                      ) : isFreeUser && (
                        <Link
                          to="/dashboard?tab=subscription"
                          className="ml-1 font-medium underline hover:text-foreground"
                        >
                          {t('subscription.oneTimeTopUp')}
                        </Link>
                      )}
                    </p>
                  )}
                </div>

                {user && !user.isAnonymous && (
                  <GamePassFundingChoice
                    summary={gamePassSummary}
                    source={selectedFundingSource}
                    onChange={(source) => {
                      setFundingSource(source)
                      setValidationError('')
                    }}
                    freeMode={isFreeBillingMode}
                    excluded={gameSettingForGame === 'MODEL_COMPARISON' ? 'comparison' : gameSettingForGame === 'BYOM' ? 'byom' : null}
                  />
                )}
                {
                }
                {isMultiplayer && (
                  <div className="space-y-1 pt-4">
                    <Label htmlFor="room-name-input" className="text-xs">
                      {t('game.room.nameLabel')}
                    </Label>
                    <Input
                      id="room-name-input"
                      ref={roomNameInputRef}
                      required
                      value={roomName}
                      maxLength={ROOM_NAME_MAX_LENGTH}
                      placeholder={t('game.room.namePlaceholder')}
                      data-testid="room-name-input"
                      onChange={event => setRoomName(event.target.value)}
                    />
                  </div>
                )}

                {isMultiplayer && !isFreeBillingMode && (
                  <fieldset className="space-y-2" data-testid="room-payment-options">
                    <legend className="mb-2 text-xs font-medium">
                      {t('game.room.paymentModeLabel')}
                    </legend>
                    {(['split', 'host'] as const).map(mode => (
                      <label
                        key={mode}
                        className="flex min-h-11 cursor-pointer items-center gap-2 text-sm text-foreground"
                      >
                        <input
                          type="radio"
                          name="room-payment-mode"
                          value={mode}
                          checked={roomPaymentMode === mode}
                          onChange={() => setRoomPaymentMode(mode)}
                          className="size-4 shrink-0 accent-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                          data-testid={`room-payment-${mode}`}
                        />
                        <span>
                          {t(mode === 'host'
                            ? 'game.room.paymentOptionHost'
                            : 'game.room.paymentOptionSplit')}
                        </span>
                      </label>
                    ))}
                    <p className="text-xs text-muted-foreground">
                      {t('game.room.paymentModeFixed')}
                    </p>
                  </fieldset>
                )}

                {

                }
                <SevenDayCheckInStrip />
                <div
                  className="flex flex-col gap-3 pt-4"
                  data-testid="launcher-actions"
                >
                  {
                  }
                  <Button
                    onClick={() => handlePrimaryAction(false)}
                    disabled={primaryActionIsDisabled}
                    aria-busy={
                      isStartAccessLoading || primaryActionIsPending
                        ? true
                        : undefined
                    }
                    aria-describedby={
                      startAccessRestriction
                        ? 'start-game-lock-reason'
                        : displayedActionError
                          ? 'start-game-action-error'
                          : undefined
                    }
                    title={startAccessRestrictionReason || undefined}
                    data-start-access-loading={
                      isStartAccessLoading ? 'true' : undefined
                    }
                    data-start-restriction={startAccessRestriction?.resolution}
                    data-testid={
                      isMultiplayer ? 'create-room-button' : 'start-game-button'
                    }
                    className={gameStartButtonClassName}
                  >
                    <span className="relative z-10 inline-flex items-center gap-2">
                      {primaryActionIsPending || isStartAccessLoading ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : startAccessRestriction ? (
                        <Lock
                          className="size-4"
                          data-testid="start-game-lock"
                          aria-hidden="true"
                        />
                      ) : null}
                      <span className="font-bold">
                        {isMultiplayer
                          ? primaryActionIsPending
                            ? t('game.room.creating')
                            : t('game.room.createPrivateRoom')
                          : isStarting
                            ? t('game.starting')
                            : t('game.startGame')}
                      </span>
                    </span>
                  </Button>

                  {isMultiplayer && (
                    <Button
                      type="button"
                      onClick={() => handlePrimaryAction(true)}
                      disabled={primaryActionIsDisabled}
                      aria-busy={isStartAccessLoading || primaryActionIsPending}
                      data-testid="create-world-room-button"
                      title={t('game.room.createWorldRoomHint')}
                      className="group relative inline-flex w-full items-center justify-center gap-2 overflow-hidden rounded-xl border-2 border-sky-400/35 bg-gradient-to-br from-sky-300 via-cyan-400 to-sky-500 px-6 py-5 text-sm font-medium text-sky-950 shadow-lg shadow-sky-500/55 backdrop-blur-md transition-all hover:border-sky-500/55 hover:from-cyan-400 hover:via-sky-500 hover:to-cyan-600 hover:shadow-xl hover:shadow-sky-600/65 dark:border-sky-500/35 dark:from-sky-400 dark:via-cyan-500 dark:to-sky-600 dark:text-black dark:shadow-sky-500/55 sm:px-8 sm:py-6"
                    >
                      <span className="relative z-10 inline-flex items-center gap-2">
                        {primaryActionIsPending || isStartAccessLoading
                          ? <Loader2 className="size-4 animate-spin" />
                          : <Globe className="size-4" aria-hidden="true" />}
                        <span className="font-bold">
                          {primaryActionIsPending
                            ? t('game.room.creating')
                            : t('game.room.createWorldRoom')}
                        </span>
                      </span>
                    </Button>
                  )}

                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleResumeGames}
                    data-testid="resume-game-button"
                    className="w-full justify-center rounded-xl px-6 py-5 sm:px-8 sm:py-6"
                  >
                    <Play className="size-4" />
                    {t('game.room.resumeGame')}
                  </Button>
                </div>

                { }
                {exceedsMaxPlayers && (
                  <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700 dark:border-amber-800/30 dark:bg-amber-950/20 dark:text-amber-300">
                    {t('game.maxPlayersExceeded')
                      .replace('{max}', String(currentMaxPlayers))
                      .replace('{current}', String(totalPlayerCount))}
                  </div>
                )}

                {displayedActionError && (
                  <div
                    id={
                      startAccessRestriction
                        ? 'start-game-lock-reason'
                        : 'start-game-action-error'
                    }
                    data-testid={
                      startAccessRestriction
                        ? 'start-game-lock-reason'
                        : 'start-game-action-error'
                    }
                    role="status"
                    className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700 dark:border-amber-800/30 dark:bg-amber-950/20 dark:text-amber-300"
                  >
                    {displayedActionError}
                    {accountCalendar.isError && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="ml-2"
                        onClick={() => { void accountCalendar.initialize().catch(() => {}) }}
                      >
                        {t('social.retry')}
                      </Button>
                    )}
                    {displayedActionError
                      === t('game.error.login_required_for_mode') && (
                      <Link
                        to={signInUrlReturningTo(returnTargetFrom(window.location))}
                        className="ml-1 font-medium underline hover:text-amber-900 dark:hover:text-amber-100"
                      >
                        {t('nav.signIn')}
                      </Link>
                    )}
                    {displayedActionError
                      === t('game.error.game_mode_restricted') && (
                      <Link
                        to="/dashboard?tab=subscription"
                        className="ml-1 font-medium underline hover:text-amber-900 dark:hover:text-amber-100"
                      >
                        {t('subscription.oneTimeTopUp')}
                      </Link>
                    )}
                    {displayedActionError
                      === t('game.error.credits_exhausted') && (
                      <Link
                        to="/dashboard"
                        className="ml-1 font-medium underline hover:text-amber-900 dark:hover:text-amber-100"
                      >
                        {t('game.credits_exhausted_topup')}
                      </Link>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          { }
          <Card className="lg:col-span-2">
            <CardContent>
              <div className="space-y-6">
                { }
                <div className="space-y-6">
                  <div>
                    {(readSettingMode(gameSettingForGame).symbolicPlayerCount !== null) && (
                      <div className="mb-6 rounded-lg border border-amber-500/30 bg-amber-500/10 p-4">
                        <div className="text-sm text-amber-800 dark:text-amber-200">
                          <p className="mb-2">{t('game.g10RandomInfoIntro')}</p>
                          <ul className="mb-2 ml-4 list-inside list-disc space-y-1">
                            <li>{t('game.g10RandomInfoEvil')}
                            </li>
                            <li>{t('game.g10RandomInfoGood')}</li>
                          </ul>
                          <p className="text-sm italic">
                            {t('game.g10RandomInfoNote')}
                          </p>
                          {roleCodeString && (
                            <div className="mt-3 rounded bg-amber-100 p-2 font-mono text-xs dark:bg-amber-900/30">
                              <strong>{t('game.roleCodeString')}:</strong>{' '}
                              {roleCodeString}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    {gameSettingForGame === 'USER_CUSTOM_CONFIG' && (
                      <div className="mb-6 rounded-lg border border-amber-500/30 bg-amber-500/10 p-4">
                        <div className="text-sm text-amber-800 dark:text-amber-200">
                          <p className="mb-2">
                            {t('game.userCustomInfoIntro')}
                          </p>
                          <ul className="list-inside list-disc space-y-1">
                            <li>{t('game.userCustomInfo2')}</li>
                          </ul>
                        </div>
                      </div>
                    )}
                    {gameSettingForGame === 'HUMAN_VS_AI' && (
                      <div className="mb-6 rounded-lg border border-purple-500/30 bg-purple-500/10 p-4">
                        <div className="text-sm text-purple-800 dark:text-purple-200">
                          <p className="mb-2 font-semibold">
                            {t('game.humanVsAIInfoIntro')}
                          </p>
                          <ul className="list-inside list-disc space-y-1">
                            <li>{t('game.humanVsAIInfo')}</li>
                            <li>{t('game.humanVsAIInfo2')}</li>
                          </ul>
                        </div>
                      </div>
                    )}
                    {gameSettingForGame === 'WATCH_MODE' && (
                      <div className="mb-6 rounded-lg border border-cyan-500/30 bg-cyan-500/10 p-4">
                        <div className="text-sm text-cyan-800 dark:text-cyan-200">
                          <p className="mb-2 font-semibold">
                            {t('game.watchModeInfoIntro')}
                          </p>
                          <ul className="list-inside list-disc space-y-1">
                            <li>{t('game.watchModeInfo')}</li>
                            <li>{t('game.watchModeInfo2')}</li>
                          </ul>
                        </div>
                      </div>
                    )}
                    {gameSettingForGame === 'MODEL_COMPARISON' && (
                      <div className="mb-6 rounded-lg border border-indigo-500/30 bg-indigo-500/10 p-4">
                        <div className="text-sm text-indigo-800 dark:text-indigo-200">
                          <p className="mb-2 font-semibold">
                            {t('game.comparisonModeInfoIntro')}
                          </p>
                          <ul className="list-inside list-disc space-y-1">
                            <li>{t('game.comparisonModeInfo')}</li>
                            <li>{t('game.comparisonModeInfo2')}</li>
                          </ul>
                        </div>
                      </div>
                    )}
                    {gameSettingForGame === 'BYOM' && (
                      <div className="mb-6 rounded-lg border border-sky-500/30 bg-sky-500/10 p-4">
                        <div className="text-sm text-sky-800 dark:text-sky-200">
                          <p className="mb-2 font-semibold">
                            {t('game.byomInfoIntro')}
                          </p>
                          { }
                          {hasCustomProvider && (
                            <div className="mb-3 flex items-center gap-2 rounded-md border border-sky-400/40 bg-sky-100/60 px-3 py-2 dark:border-sky-600/30 dark:bg-sky-900/30">
                              <span className="text-xs font-medium text-sky-600 dark:text-sky-400">
                                {t('game.byomModelIdLabel')}:
                              </span>
                              <code className="rounded bg-sky-200/60 px-1.5 py-0.5 text-xs font-semibold text-sky-900 dark:bg-sky-800/50 dark:text-sky-100">
                                {customEndpointData?.customModelId
                                  || 'Custom Model'}
                              </code>
                            </div>
                          )}
                          <ul className="list-inside list-disc space-y-1">
                            <li>{t('game.byomInfoCompete')}</li>
                            <li>{t('game.byomInfo2')}</li>
                            <li>
                              {t('game.byomConfigureModelPrefix')}
                              <Link
                                to="/dashboard?tab=byom"
                                className="font-medium underline hover:text-sky-900 dark:hover:text-sky-100"
                              >
                                {t('game.byomConfigureModelLink')}
                              </Link>
                              {t('game.byomConfigureModelSuffix')}
                            </li>
                            <li>
                              {t('game.byomContactSupport')}
                              <a
                                href={buildSupportContactUrl()}
                                className="font-medium underline hover:text-sky-900 dark:hover:text-sky-100"
                              >
                                {t('game.byomInfo3Link')}
                              </a>
                              .
                            </li>
                          </ul>
                        </div>
                      </div>
                    )}
                    <div className="mb-4 flex items-center justify-between">
                      <h3 className="text-lg font-semibold">
                        {t('game.roleConfiguration')}
                      </h3>
                      {(gameSettingForGame === 'USER_CUSTOM_CONFIG'
                        || gameSettingForGame === 'HUMAN_VS_AI'
                        || gameSettingForGame === 'WATCH_MODE'
                        || gameSettingForGame === 'MODEL_COMPARISON'
                        || gameSettingForGame === 'BYOM') && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleUnselectAll}
                          className="h-8 border-foreground/20 px-3 text-xs text-foreground hover:bg-accent hover:text-accent-foreground"
                        >
                          {t('game.unselectAll')}
                        </Button>
                      )}
                    </div>
                  </div>

                  {(isVariableSeatBoard(gameSettingForGame) || usesCustomRosterTemplate(gameSettingForGame) || (readSettingMode(gameSettingForGame).symbolicPlayerCount !== null)) && (
                    <div className="mb-6 space-y-2" data-testid="board-player-count">
                      <div
                        role="radiogroup"
                        aria-label={t('game.numberOfPlayers')}
                        className="grid w-fit grid-cols-2 gap-2"
                        onKeyDown={(event) => {
                          if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key))
                            return
                          event.preventDefault()
                          const buttons = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('button[role="radio"]'))
                            .filter(button => !button.disabled)
                          const index = buttons.indexOf(document.activeElement as HTMLButtonElement)
                          const nextIndex = event.key === 'Home'
                            ? 0
                            : event.key === 'End'
                              ? buttons.length - 1
                              : (index + (event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 1) + buttons.length) % buttons.length
                          buttons[nextIndex]?.click()
                          buttons[nextIndex]?.focus()
                        }}
                      >
                        {[10, 12].map((playerCount) => {
                          const selectedSetting = (readSettingMode(gameSettingForGame).symbolicPlayerCount !== null)
                            ? playerCount === 10 ? GameSettingOptions.G10_ULTIMATE_TRIAL : GameSettingOptions.G12_ULTIMATE_TRIAL
                            : playerCount === 10
                              ? tenSeatSettingFor(gameSettingForGame)
                              : boardTabSettingFor(gameSettingForGame)
                          const isSelected = usesCustomRosterTemplate(gameSettingForGame)
                            ? customPlayerCount === playerCount
                            : gameSettingForGame === selectedSetting
                          const isDisabled = isFreeBillingMode && playerCount === 12
                          return (
                            <button
                              key={playerCount}
                              type="button"
                              role="radio"
                              aria-checked={isSelected}
                              tabIndex={!isDisabled && (isSelected || isFreeBillingMode) ? 0 : -1}
                              disabled={isDisabled}
                              data-testid={`player-count-${playerCount}`}
                              onClick={() => usesCustomRosterTemplate(gameSettingForGame)
                                ? setCustomPlayerCount(playerCount as CustomRosterPlayerCount)
                                : handleGameSettingChange(selectedSetting)}
                              className={`inline-flex h-9 !min-w-[104px] shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-md border px-2 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 motion-reduce:transition-none max-[780px]:h-11 ${
                                isDisabled
                                  ? 'cursor-not-allowed border-[#e0e2e6] bg-transparent text-[#8a9098] dark:border-[#80919d]/[0.23] dark:text-[#80919d]'
                                  : isSelected
                                    ? 'border-[#b59d72] bg-[#fdf8ec] text-[#62440f] dark:border-[#c3a065] dark:bg-primary/[0.09] dark:text-primary'
                                    : 'border-[#d7d9dd] bg-transparent text-foreground hover:bg-[#f8f7f2] dark:border-muted-foreground/[0.28] dark:hover:bg-primary/[0.05]'
                              }`}
                            >
                              {isSelected && !isDisabled && <Check className="size-3 shrink-0" strokeWidth={2.3} aria-hidden="true" />}
                              {isDisabled && <Lock className="size-3" aria-hidden="true" />}
                              <span>{t(playerCount === 10 ? 'game.players10' : 'game.players12')}</span>
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  )}

                  {(readSettingMode(gameSettingForGame).symbolicPlayerCount !== null) ? (
                    <BrawlRosterConfiguration
                      playerCount={readSettingMode(gameSettingForGame).symbolicPlayerCount!}
                      wolfSiblingPublicEnabled={wolfSiblingPublicEnabled}
                      showModels={isModelConfigurationVisible}
                      t={t}
                      roleName={key => getRoleName(t, key)}
                      seatName={key => getModelSeatName(t, key)}
                      renderModel={renderCustomSeatControls}
                    />
                  ) : usesCustomRosterTemplate(gameSettingForGame) ? (
                    <CustomRosterConfiguration
                      selectedRoles={selectedCustomRoles}
                      villagers={selectedCustomVillagers}
                      extraRole={customExtraRole}
                      wolfChoice={customWolfChoice}
                      wolfSiblingPublicEnabled={wolfSiblingPublicEnabled || gameSettingForGame === 'HUMAN_VS_AI'}
                      isRoleAllowed={gameSettingForGame === 'HUMAN_VS_AI' ? role => !(ROSTER_CHOICES.humanVsAiDisabledRoles as readonly string[]).includes(role) : undefined}
                      showModels={isModelConfigurationVisible}
                      t={t}
                      roleName={key => getRoleName(t, key)}
                      seatName={key => getModelSeatName(t, key)}
                      onSelect={handleRoleToggle}
                      renderModel={renderCustomSeatControls}
                    />
                  ) : (
                    <>
                      { }
                      <div className="space-y-3">
                        <Label className="text-sm font-bold">
                          {t('game.specialRoles')}
                          <span className="font-bold">
                            (
                            {(readSettingMode(gameSettingForGame).symbolicPlayerCount !== null)
                              ? '?'
                              : specialRolesCount}
                            )
                          </span>
                        </Label>
                        <div
                          className={`grid gap-3 pl-2 ${isModelConfigurationVisible && (supportsPlatformRoleModelSelection || gameSettingForGame === 'BYOM') ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-2 md:grid-cols-3'}`}
                        >
                          {getRoleConfiguration(gameSettingForGame).goodRoles.map(
                            (role) => {
                              const isFixedG10Role
                                = (readSettingMode(gameSettingForGame).symbolicPlayerCount !== null)
                                  && role.key === 'seer'
                              const enabled
                                = isFixedG10Role
                                  || isRoleEnabled(role.key, role.enabled)
                              const isQuestionMark
                                = (readSettingMode(gameSettingForGame).symbolicPlayerCount !== null)
                                  && !isFixedG10Role
                              return (
                                <div
                                  key={role.key}
                                  className="flex items-center gap-1.5"
                                >
                                  {isQuestionMark ? (
                                    <div className="flex size-4 shrink-0 items-center justify-center rounded-sm border border-primary bg-muted">
                                      <span className="text-xs font-bold text-muted-foreground">
                                        ?
                                      </span>
                                    </div>
                                  ) : (
                                    <Checkbox
                                      id={`good-${role.key}`}
                                      checked={enabled}
                                      disabled={isCheckboxDisabled(role.key)}
                                      onCheckedChange={
                                        isFixedG10Role
                                          ? undefined
                                          : () => handleRoleToggle(role.key, true)
                                      }
                                    />
                                  )}
                                  <Label
                                    htmlFor={`good-${role.key}`}
                                    className={`shrink-0 text-sm font-normal text-gray-900 !opacity-100 dark:text-gray-100 ${isModelConfigurationVisible && (supportsPlatformRoleModelSelection || gameSettingForGame === 'BYOM') ? 'min-w-20' : ''}`}
                                  >
                                    {getRoleName(t, role.key)}
                                  </Label>
                                  {renderPlatformModelSelector(
                                    role.key,
                                    runtimeSeatKeyBySource.get(role.key)
                                    ?? role.key,
                                    enabled || isQuestionMark,
                                  )}
                                  {gameSettingForGame === 'BYOM'
                                    && isModelConfigurationVisible
                                    && enabled
                                    && (availableModels.length > 0
                                      || hasCustomProvider) && (
                                    <select
                                      value={byomRoleAssignments[role.key] || ''}
                                      onChange={e =>
                                        setByomRoleAssignments((prev) => {
                                          const next = { ...prev }
                                          if (e.target.value) {
                                            next[role.key] = e.target.value
                                          }
                                          else {
                                            delete next[role.key]
                                          }
                                          return next
                                        })}
                                      className="h-6 max-w-[160px] truncate rounded-md border border-amber-400/40 bg-gradient-to-r from-amber-500/10 to-amber-500/5 px-1.5 text-[11px] text-foreground transition-colors hover:border-amber-400/60 focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500/40 dark:from-amber-500/15 dark:to-amber-500/5"
                                    >
                                      {availableModels.map(m => (
                                        <option key={m} value={m}>
                                          {getModelDisplayName(m)}
                                        </option>
                                      ))}
                                      {hasCustomProvider && (
                                        <option value="custom">
                                          (You) {customModelLabel}
                                        </option>
                                      )}
                                    </select>
                                  )}
                                </div>
                              )
                            },
                          )}
                        </div>

                        { }
                        <div className="space-y-3">
                          <Label className="text-sm font-medium">
                            {t('game.villagers')}{' '}
                            <span className="font-bold">
                              (
                              {(readSettingMode(gameSettingForGame).symbolicPlayerCount !== null)
                                ? '?'
                                : getVillagerCount()}
                              )
                            </span>
                          </Label>
                          {gameSettingForGame === 'G6_1SR1WT_2WW_2VG'
                            || gameSettingForGame === 'G9_1HT1SR1WT_3WW_3VG' ? (
                                <div className="grid grid-cols-1 gap-3 pl-2 sm:grid-cols-2">
                                  {Array.from(
                                    { length: getVillagerCount() },
                                    (_, index) => {
                                      const villagerKey = `villager_${index}`
                                      return (
                                        <div
                                          key={villagerKey}
                                          className="flex items-center gap-1.5"
                                        >
                                          <Checkbox
                                            id={`villager-${index}`}
                                            checked
                                            disabled
                                          />
                                          <Label htmlFor={`villager-${index}`} className="min-w-20 shrink-0 text-sm font-normal text-gray-900 !opacity-100 dark:text-gray-100">
                                            {getRoleName(t, 'villager')} #{index + 1}
                                          </Label>
                                          {renderPlatformModelSelector(
                                            villagerKey,
                                            runtimeSeatKeyBySource.get(villagerKey)
                                            ?? villagerKey,
                                            true,
                                          )}
                                        </div>
                                      )
                                    },
                                  )}
                                </div>
                              ) : (
                                <div
                                  className={`grid gap-3 pl-2 ${isModelConfigurationVisible && (supportsPlatformRoleModelSelection || gameSettingForGame === 'BYOM') ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-2 md:grid-cols-4'}`}
                                >
                                  {villagerCheckboxes.map(
                                    ({
                                      index,
                                      enabled,
                                      isQuestionMark,
                                      isFixedG10Villager,
                                    }) => {
                                      const villagerKey = `villager_${index}`
                                      return (
                                        <div
                                          key={index}
                                          className="flex items-center gap-1.5"
                                        >
                                          {isQuestionMark ? (
                                            <div className="flex size-4 shrink-0 items-center justify-center rounded-sm border border-primary bg-muted">
                                              <span className="text-xs font-bold text-muted-foreground">
                                                ?
                                              </span>
                                            </div>
                                          ) : (
                                            <Checkbox
                                              id={`villager-${index}`}
                                              checked={enabled}
                                              disabled={isVillagerCheckboxDisabled()}
                                              onCheckedChange={
                                                isFixedG10Villager
                                                  ? undefined
                                                  : () => handleVillagerToggle(index)
                                              }
                                            />
                                          )}
                                          <Label
                                            htmlFor={`villager-${index}`}
                                            className={`shrink-0 text-sm font-normal text-gray-900 !opacity-100 dark:text-gray-100 ${isModelConfigurationVisible && (supportsPlatformRoleModelSelection || gameSettingForGame === 'BYOM') ? 'min-w-20' : ''}`}
                                          >
                                            {getRoleName(t, 'villager')} #{index + 1}
                                          </Label>
                                          {renderPlatformModelSelector(
                                            villagerKey,
                                            runtimeSeatKeyBySource.get(villagerKey)
                                            ?? villagerKey,
                                            enabled || isQuestionMark,
                                          )}
                                          {gameSettingForGame === 'BYOM'
                                            && isModelConfigurationVisible
                                            && enabled
                                            && (availableModels.length > 0
                                              || hasCustomProvider) && (
                                            <select
                                              value={byomRoleAssignments[villagerKey] || ''}
                                              onChange={e =>
                                                setByomRoleAssignments(prev => ({
                                                  ...prev,
                                                  [villagerKey]: e.target.value,
                                                }))}
                                              className="h-6 max-w-[160px] truncate rounded-md border border-amber-400/40 bg-gradient-to-r from-amber-500/10 to-amber-500/5 px-1.5 text-[11px] text-foreground transition-colors hover:border-amber-400/60 focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500/40 dark:from-amber-500/15 dark:to-amber-500/5"
                                            >
                                              <option value="">
                                                {t('game.selectModel')}
                                              </option>
                                              {availableModels.map(m => (
                                                <option key={m} value={m}>
                                                  {getModelDisplayName(m)}
                                                </option>
                                              ))}
                                              {hasCustomProvider && (
                                                <option value="custom">
                                                  (You) {customModelLabel}
                                                </option>
                                              )}
                                            </select>
                                          )}
                                        </div>
                                      )
                                    },
                                  )}
                                </div>
                              )}
                        </div>
                      </div>

                      { }
                      <div className="my-6 border-t border-gray-200 dark:border-gray-700" />

                      { }
                      <div className="space-y-3">
                        <Label className="text-sm font-bold">
                          {t('game.evilFaction')}
                          <span className="font-bold">
                            (
                            {(readSettingMode(gameSettingForGame).symbolicPlayerCount !== null)
                              ? '?'
                              : evilRolesCount}
                            )
                          </span>
                        </Label>
                        <div
                          className={`grid gap-3 pl-2 ${isModelConfigurationVisible && (supportsPlatformRoleModelSelection || gameSettingForGame === 'BYOM') ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-2 md:grid-cols-3'}`}
                        >
                          {getRoleConfiguration(gameSettingForGame)
                            .evilRoles
                            .filter(role => role.key !== WOLF_SIBLING_PAIR_KEY
                              || wolfSiblingPublicEnabled
                              || gameSettingForGame === 'HUMAN_VS_AI')
                            .flatMap((role, roleIndex) => expandWolfSiblingChoice(role.key)
                              .map(seatKey => ({ role, seatKey, roleIndex })))
                            .map(({ role, seatKey, roleIndex }) => {
                              const isHumanVsAiRoleDisabled
                                = isRoleDisabledForHumanVsAi(gameSettingForGame, role.key)
                              const isFixedG10Role
                                = (readSettingMode(gameSettingForGame).symbolicPlayerCount !== null)
                                  && role.key === 'werewolf'
                              const enabled
                                = !isHumanVsAiRoleDisabled
                                  && (isFixedG10Role
                                    || isRoleEnabled(role.key, role.enabled))
                              const uniqueKey
                                = seatKey === 'werewolf'
                                  ? `${seatKey}-${roleIndex}`
                                  : seatKey
                              const isQuestionMark
                                = (readSettingMode(gameSettingForGame).symbolicPlayerCount !== null)
                                  && !isFixedG10Role
                              return (
                                <div
                                  key={uniqueKey}
                                  className="flex items-center gap-1.5"
                                >
                                  {isQuestionMark ? (
                                    <div className="flex size-4 shrink-0 items-center justify-center rounded-sm border border-primary bg-muted">
                                      <span className="text-xs font-bold text-muted-foreground">
                                        ?
                                      </span>
                                    </div>
                                  ) : isHumanVsAiRoleDisabled ? (
                                    <div
                                      data-testid={`human-vs-ai-disabled-${seatKey}`}
                                      className="flex size-4 shrink-0 items-center justify-center rounded-sm border border-destructive bg-destructive/10"
                                    >
                                      <span className="text-xs font-bold text-destructive">
                                        ✕
                                      </span>
                                    </div>
                                  ) : (
                                    <Checkbox
                                      id={`evil-${uniqueKey}`}
                                      checked={enabled}
                                      disabled={isCheckboxDisabled(role.key)}
                                      onCheckedChange={
                                        isFixedG10Role
                                          ? undefined
                                          : () => handleRoleToggle(role.key, false)
                                      }
                                    />
                                  )}
                                  <Label
                                    htmlFor={
                                      isQuestionMark || isHumanVsAiRoleDisabled
                                        ? undefined
                                        : `evil-${uniqueKey}`
                                    }
                                    className={`shrink-0 text-sm font-normal ${isHumanVsAiRoleDisabled ? 'text-muted-foreground' : 'text-gray-900 !opacity-100 dark:text-gray-100'} ${isModelConfigurationVisible && (supportsPlatformRoleModelSelection || gameSettingForGame === 'BYOM') ? 'min-w-20' : ''}`}
                                  >
                                    {getModelSeatName(t, seatKey)}
                                  </Label>
                                  <div className="flex min-w-0 items-center gap-1">
                                    {renderPlatformModelSelector(
                                      seatKey,
                                      runtimeSeatKeyBySource.get(seatKey) ?? seatKey,
                                      enabled || isQuestionMark,
                                    )}
                                    {gameSettingForGame === 'BYOM'
                                      && isModelConfigurationVisible
                                      && enabled
                                      && (availableModels.length > 0 || hasCustomProvider) && (
                                      <select
                                        data-testid={`byom-model-select-${seatKey}`}
                                        value={byomRoleAssignments[seatKey] || ''}
                                        onChange={e =>
                                          setByomRoleAssignments(prev => ({
                                            ...prev,
                                            [seatKey]: e.target.value,
                                          }))}
                                        className="h-6 max-w-[160px] truncate rounded-md border border-amber-400/40 bg-gradient-to-r from-amber-500/10 to-amber-500/5 px-1.5 text-[11px] text-foreground transition-colors hover:border-amber-400/60 focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500/40 dark:from-amber-500/15 dark:to-amber-500/5"
                                      >
                                        {availableModels.map(m => (
                                          <option key={m} value={m}>{getModelDisplayName(m)}</option>
                                        ))}
                                        {hasCustomProvider && (
                                          <option value="custom">(You) {customModelLabel}</option>
                                        )}
                                      </select>
                                    )}
                                  </div>
                                </div>
                              )
                            },
                            )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
